import type { AppLocale, Messages } from "./types";

const en: Messages = {
  metaTitle: "Invitation",
  invitePageTitleFallbackWedding: "Wedding",
  loading: "Loading…",
  invitationNotFound: "Invitation not found",
  invalidInviteLink:
    "Open a valid invitation link, e.g. /your-slug, /invite/your-slug, or ?invite=your-slug on localhost.",
  invitationUnavailable: "This invitation does not exist or is unavailable.",
  unknownError: "Unknown error",
  noPhoto: "No photo",
  noPhotoAria: "No photo",
  location: "Location",
  about: "About",
  timeline: "Timeline",
  rsvp: "RSVP",
  lastDay: "Last day",
  countdownDays: "d",
  countdownHours: "h",
  countdownMinutes: "min",
  enjoyCelebration: "Enjoy the celebration",
  openAppleMaps: "Open directions in Apple Maps",
  openGoogleMaps: "Open directions in Google Maps",
  appleMaps: "Apple Maps",
  googleMaps: "Google Maps",
  venueMapAria: "Venue map",
  rsvpCapacityOne: "This event accepts up to 1 guest on this invitation.",
  rsvpCapacityMany: "This event accepts up to {n} guests on this invitation.",
  rsvpHelpText:
    "Please enter at least one name. Other rows are optional if you are coming alone.",
  guestNumber: "Guest {n}",
  fullNamePlaceholder: "Full name",
  optionalNamePlaceholder: "Optional — full name",
  mealPreferencesOptional: "Meal preferences (optional)",
  mealVegetarian: "Vegetarian",
  mealVegan: "Vegan",
  mealGlutenFree: "Gluten free",
  mealNutAllergy: "Nut allergy",
  submitRsvp: "Submit RSVP",
  rsvpNotEnabled: "RSVP is not enabled for this invitation.",
  rsvpDeadlinePassed: "The RSVP deadline has passed.",
  rsvpNotReady: "RSVP is not ready yet. The hosts need to set a guest limit in the app.",
  nameTooLong: "Name is too long.",
  invalidData: "Invalid data.",
  enterGuestName: "Please enter at least one guest name.",
  rsvpThankYou: "Thank you! Your RSVP was received.",
  rsvpSubmitError: "Could not submit. Please try again.",
  rsvpSaveChanges: "Save changes",
  rsvpWithdraw: "Delete my RSVP",
  rsvpWithdrawDialogTitle: "Delete your RSVP?",
  rsvpWithdrawDialogBody:
    "Hosts will no longer see this response. If the deadline still allows, you can submit a new RSVP afterward.",
  rsvpWithdrawConfirm: "Delete RSVP",
  rsvpWithdrawCancel: "Cancel",
  rsvpWithdrawnToast: "Your RSVP was removed.",
  rsvpSessionNote:
    "This browser remembers your reply (anonymous sign-in). Clearing site data or using another device may require responding again.",
  rsvpAuthError: "Could not start your session. Please refresh the page.",
  appPromoText: "Get the iPhone app to create and manage your invitations.",
  downloadAppStoreAria: "Download on the App Store",
  dateTBD: "Date TBD",
  scheduleStarts: "Starts",
  scheduleEnds: "Ends",
  weatherTitle: "Weather",
  weatherForecastTooFar: "The daily forecast will appear here two weeks before the wedding.",
  weatherPastEvent: "This event date has passed — no forecast shown.",
  weatherLoadError: "Weather couldn’t load. Try again later.",
  weatherNoData: "No forecast for this day yet.",
  weatherTempRange: "{min}° / {max}°",
  weatherUnitCelsius: "°C",
  weatherUnitFahrenheit: "°F",
  weatherCeremonyLine: "At ceremony time ({time}): {temp}, {summary}.",
  weatherRainChanceDay: "Chance of rain: {pct}%",
  weatherAriaTempCelsius: "Show temperatures in Celsius",
  weatherAriaTempFahrenheit: "Show temperatures in Fahrenheit",
  weatherClear: "Clear",
  weatherCloudy: "Cloudy",
  weatherFog: "Fog",
  weatherDrizzle: "Drizzle",
  weatherRain: "Rain",
  weatherSnow: "Snow",
  weatherRainShowers: "Rain showers",
  weatherSnowShowers: "Snow showers",
  weatherThunderstorm: "Thunderstorm",
  weatherUnknown: "Mixed conditions",
  addToCalendar: "Add to calendar",
  addToCalendarAria: "Download a calendar file (.ics) for this invitation",
  songRequestTitle: "Song requests",
  songRequestIntro:
    "Add your name, then paste or drop Spotify or Apple Music song links into up to five tiles. A short preview appears once each link is recognized.",
  songRequestRequesterLabel: "Your name",
  songRequestRequesterPlaceholder: "Who is requesting?",
  songRequestLinkLabel: "Track link",
  songRequestLinkPlaceholder: "Spotify or Apple Music URL",
  songRequestAddTrack: "Add another song",
  songRequestRemoveTrack: "Remove",
  songRequestPreviewLoading: "Looking up track…",
  songRequestPreviewError: "Couldn’t read this link. Check that it’s a Spotify or Apple Music song.",
  songRequestInvalidLink: "Use a Spotify or Apple Music song link.",
  songRequestLimitReached: "You can add at most five songs.",
  songRequestSubmit: "Send requests",
  songRequestThankYou: "Thanks! Your song requests were sent.",
  songRequestSubmitError: "Could not send. Please try again.",
  songRequestNeedName: "Please enter your name.",
  songRequestNeedOneResolvedTrack: "Fill at least one tile and wait until the preview appears.",
  songRequestSpotifyAria: "Spotify",
  songRequestAppleMusicAria: "Apple Music",
  songRequestSlotsCaption: "Tap a tile, then paste (Ctrl+V / ⌘V) or drop a link. Five song slots — only filled tiles are sent.",
  songRequestSlotPasteHint: "Paste or drop",
  songRequestSlotAria: "Song slot {n}. Tap, then paste or drop a Spotify or Apple Music link.",
};

const tr: Messages = {
  ...en,
  invitePageTitleFallbackWedding: "Düğün",
  metaTitle: "Davetiye",
  loading: "Yükleniyor…",
  invitationNotFound: "Davetiye bulunamadı",
  invalidInviteLink:
    "Geçerli bir davet bağlantısı kullanın: /kisa-ad, /invite/kisa-ad veya ?invite=kisa-ad (localhost).",
  invitationUnavailable: "Bu davetiye mevcut değil veya kullanılamıyor.",
  unknownError: "Bilinmeyen hata",
  noPhoto: "Fotoğraf yok",
  noPhotoAria: "Fotoğraf yok",
  location: "Konum",
  about: "Hakkında",
  timeline: "Program",
  rsvp: "Katılım",
  lastDay: "Son gün",
  countdownDays: "g",
  countdownHours: "s",
  countdownMinutes: "dk",
  enjoyCelebration: "Eğlencenin tadını çıkarın",
  openAppleMaps: "Apple Haritalar’da yol tarifi aç",
  openGoogleMaps: "Google Haritalar’da yol tarifi aç",
  appleMaps: "Apple Haritalar",
  googleMaps: "Google Haritalar",
  venueMapAria: "Mekân haritası",
  rsvpCapacityOne: "Bu davetiye ile etkinliğe en fazla 1 misafir kabul edilir.",
  rsvpCapacityMany: "Bu davetiye ile etkinliğe en fazla {n} misafir kabul edilir.",
  rsvpHelpText:
    "En az bir ad yazın. Tek başınıza geliyorsanız diğer satırları doldurmanız gerekmez.",
  guestNumber: "Misafir {n}",
  fullNamePlaceholder: "Ad soyad",
  optionalNamePlaceholder: "İsteğe bağlı — ad soyad",
  mealPreferencesOptional: "Yemek tercihleri (isteğe bağlı)",
  mealVegetarian: "Vejetaryen",
  mealVegan: "Vegan",
  mealGlutenFree: "Glutensiz",
  mealNutAllergy: "Kuruyemiş alerjisi",
  submitRsvp: "Katılımı gönder",
  rsvpNotEnabled: "Bu davetiye için katılım (RSVP) kapalı.",
  rsvpDeadlinePassed: "Katılım son tarihi geçti.",
  rsvpNotReady: "Katılım henüz hazır değil. Ev sahipleri uygulamadan misafir limiti belirlemeli.",
  nameTooLong: "İsim çok uzun.",
  invalidData: "Geçersiz veri.",
  enterGuestName: "Lütfen en az bir misafir adı girin.",
  rsvpThankYou: "Teşekkürler! Katılımınız alındı.",
  rsvpSubmitError: "Gönderilemedi. Lütfen tekrar deneyin.",
  rsvpSaveChanges: "Değişiklikleri kaydet",
  rsvpWithdraw: "Yanıtımı sil",
  rsvpWithdrawDialogTitle: "Yanıtınız silinsin mi?",
  rsvpWithdrawDialogBody:
    "Ev sahipleri bu yanıtı artık göremez. Son tarih uygunsa daha sonra yeni bir katılım gönderebilirsiniz.",
  rsvpWithdrawConfirm: "Evet, sil",
  rsvpWithdrawCancel: "Vazgeç",
  rsvpWithdrawnToast: "Yanıtınız kaldırıldı.",
  rsvpSessionNote:
    "Bu tarayıcı yanıtınızı hatırlar (anonim oturum). Site verilerini silerseniz veya başka cihaz kullanırsanız tekrar yanıt vermeniz gerekebilir.",
  rsvpAuthError: "Oturum başlatılamadı. Sayfayı yenileyin.",
  appPromoText: "Davetiyelerinizi oluşturmak ve yönetmek için iPhone uygulamamızı indirin.",
  downloadAppStoreAria: "App Store’dan indir",
  dateTBD: "Tarih belirlenecek",
  scheduleStarts: "Başlangıç",
  scheduleEnds: "Bitiş",
  addToCalendar: "Takvime ekle",
  addToCalendarAria: "Bu davet için takvim dosyası (.ics) indir",
  songRequestTitle: "Şarkı istekleri",
  songRequestIntro:
    "Adınızı yazın; en fazla beş kutuya Spotify veya Apple Music şarkı bağlantısı yapıştırın veya sürükleyip bırakın. Bağlantı tanınınca kısa önizleme gösterilir.",
  songRequestRequesterLabel: "Adınız",
  songRequestRequesterPlaceholder: "Kim istiyor?",
  songRequestLinkLabel: "Parça bağlantısı",
  songRequestLinkPlaceholder: "Spotify veya Apple Music adresi",
  songRequestAddTrack: "Bir şarkı daha ekle",
  songRequestRemoveTrack: "Kaldır",
  songRequestPreviewLoading: "Parça aranıyor…",
  songRequestPreviewError: "Bu bağlantı okunamadı. Spotify veya Apple Music şarkı linki olduğundan emin olun.",
  songRequestInvalidLink: "Yalnızca Spotify veya Apple Music şarkı linki kullanın.",
  songRequestLimitReached: "En fazla 5 şarkı ekleyebilirsiniz.",
  songRequestSubmit: "İstekleri gönder",
  songRequestThankYou: "Teşekkürler! Şarkı istekleriniz gönderildi.",
  songRequestSubmitError: "Gönderilemedi. Lütfen tekrar deneyin.",
  songRequestNeedName: "Lütfen adınızı yazın.",
  songRequestNeedOneResolvedTrack: "En az bir kutuyu doldurun ve önizleme gelene kadar bekleyin.",
  songRequestSpotifyAria: "Spotify",
  songRequestAppleMusicAria: "Apple Music",
  songRequestSlotsCaption: "Bir kutuya dokunun, sonra Spotify veya Apple Music bağlantısını yapıştırın (Ctrl+V / ⌘V) veya sürükleyin. Beş şarkı kutusu — yalnızca dolu olanlar gönderilir.",
  songRequestSlotPasteHint: "Yapıştır veya bırak",
  songRequestSlotAria: "Şarkı kutusu {n}. Dokunun; Spotify veya Apple Music bağlantısı yapıştırın veya bırakın.",
};

const ar: Messages = {
  ...en,
  invitePageTitleFallbackWedding: "زفاف",
  metaTitle: "دعوة",
  loading: "جارٍ التحميل…",
  invitationNotFound: "لم يتم العثور على الدعوة",
  invalidInviteLink: "افتح رابط دعوة صالح، مثل /your-slug أو /invite/your-slug أو ?invite=your-slug على localhost.",
  invitationUnavailable: "هذه الدعوة غير موجودة أو غير متاحة.",
  unknownError: "خطأ غير معروف",
  noPhoto: "لا توجد صورة",
  noPhotoAria: "لا توجد صورة",
  location: "الموقع",
  about: "نبذة",
  timeline: "الجدول الزمني",
  rsvp: "تأكيد الحضور",
  lastDay: "آخر يوم",
  countdownDays: "ي",
  countdownHours: "س",
  countdownMinutes: "د",
  enjoyCelebration: "استمتعوا بالاحتفال",
  openAppleMaps: "افتح الاتجاهات في Apple Maps",
  openGoogleMaps: "افتح الاتجاهات في Google Maps",
  appleMaps: "Apple Maps",
  googleMaps: "Google Maps",
  venueMapAria: "خريطة المكان",
  rsvpCapacityOne: "يُقبل ضيف واحد كحد أقصى مع هذه الدعوة.",
  rsvpCapacityMany: "يُقبل حتى {n} ضيوف مع هذه الدعوة.",
  rsvpHelpText: "يرجى إدخال اسم واحد على الأقل. الصفوف الأخرى اختيارية إن كنتم تحضرون وحدكم.",
  guestNumber: "الضيف {n}",
  fullNamePlaceholder: "الاسم الكامل",
  optionalNamePlaceholder: "اختياري — الاسم الكامل",
  mealPreferencesOptional: "تفضيلات الوجبة (اختياري)",
  mealVegetarian: "نباتي",
  mealVegan: "نباتي صرف",
  mealGlutenFree: "خالٍ من الغلوتين",
  mealNutAllergy: "حساسية من المكسرات",
  submitRsvp: "إرسال تأكيد الحضور",
  rsvpNotEnabled: "تأكيد الحضور غير مفعّل لهذه الدعوة.",
  rsvpDeadlinePassed: "انتهى موعد آخر تأكيد للحضور.",
  rsvpNotReady: "تأكيد الحضور غير جاهز بعد. على المضيفين تعيين حد الضيوف في التطبيق.",
  nameTooLong: "الاسم طويل جداً.",
  invalidData: "بيانات غير صالحة.",
  enterGuestName: "يرجى إدخال اسم ضيف واحد على الأقل.",
  rsvpThankYou: "شكراً! تم استلام تأكيد حضوركم.",
  rsvpSubmitError: "تعذّر الإرسال. حاولوا مرة أخرى.",
  rsvpSaveChanges: "حفظ التغييرات",
  rsvpWithdraw: "حذف تأكيد حضوري",
  rsvpWithdrawDialogTitle: "حذف تأكيد الحضور؟",
  rsvpWithdrawDialogBody:
    "لن يعود المضيفون يرون هذا الرد. إذا ما زال الموعد مسموحاً، يمكنكم إرسال تأكيد جديد لاحقاً.",
  rsvpWithdrawConfirm: "حذف التأكيد",
  rsvpWithdrawCancel: "إلغاء",
  rsvpWithdrawnToast: "تمت إزالة تأكيد حضوركم.",
  rsvpSessionNote:
    "يتذكر هذا المتصفح ردكم (تسجيل دخول مجهول). قد تحتاجون للرد مجدداً إن مسحتم بيانات الموقع أو استخدمتم جهازاً آخر.",
  rsvpAuthError: "تعذّر بدء الجلسة. حدّثوا الصفحة.",
  appPromoText: "احصلوا على تطبيق iPhone لإنشاء دعواتكم وإدارتها.",
  downloadAppStoreAria: "التنزيل من App Store",
  dateTBD: "التاريخ غير محدد",
  scheduleStarts: "يبدأ",
  scheduleEnds: "ينتهي",
  addToCalendar: "إضافة إلى التقويم",
  addToCalendarAria: "تنزيل ملف تقويم (.ics) لهذه الدعوة",
  songRequestTitle: "طلبات الأغاني",
  songRequestIntro:
    "اكتب اسمك، ثم املأ حتى خمس مربعات بروابط أغاني Spotify أو Apple Music — الصق أو أسقِط الرابط. تظهر معاينة قصيرة عند التعرّف على الرابط.",
  songRequestRequesterLabel: "اسمك",
  songRequestRequesterPlaceholder: "من يطلب؟",
  songRequestLinkLabel: "رابط الأغنية",
  songRequestLinkPlaceholder: "رابط Spotify أو Apple Music",
  songRequestAddTrack: "إضافة أغنية أخرى",
  songRequestRemoveTrack: "إزالة",
  songRequestPreviewLoading: "جارٍ البحث عن الأغنية…",
  songRequestPreviewError: "تعذّر قراءة الرابط. تأكد أنه رابط أغنية من Spotify أو Apple Music.",
  songRequestInvalidLink: "استخدم رابط أغنية من Spotify أو Apple Music فقط.",
  songRequestLimitReached: "يمكنك إضافة خمس أغانٍ كحد أقصى.",
  songRequestSubmit: "إرسال الطلبات",
  songRequestThankYou: "شكراً! تم إرسال طلبات الأغاني.",
  songRequestSubmitError: "تعذّر الإرسال. حاولوا مرة أخرى.",
  songRequestNeedName: "يرجى إدخال اسمك.",
  songRequestNeedOneResolvedTrack: "املأ مربعاً واحداً على الأقل وانتظر ظهور المعاينة.",
  songRequestSpotifyAria: "Spotify",
  songRequestAppleMusicAria: "Apple Music",
  songRequestSlotsCaption:
    "المس مربعاً، ثم الصق (Ctrl+V / ⌘V) أو أسقِط الرابط. خمس خانات — تُرسل فقط المُعبأة.",
  songRequestSlotPasteHint: "لصق أو إسقاط",
  songRequestSlotAria: "خانة أغنية {n}. المس، ثم الصق أو أسقِط رابط Spotify أو Apple Music.",
};

const fr: Messages = {
  ...en,
  invitePageTitleFallbackWedding: "Mariage",
  metaTitle: "Invitation",
  loading: "Chargement…",
  invitationNotFound: "Invitation introuvable",
  invalidInviteLink:
    "Ouvrez un lien d’invitation valide, p. ex. /votre-slug, /invite/votre-slug ou ?invite=votre-slug sur localhost.",
  invitationUnavailable: "Cette invitation n’existe pas ou n’est pas disponible.",
  unknownError: "Erreur inconnue",
  noPhoto: "Pas de photo",
  noPhotoAria: "Pas de photo",
  location: "Lieu",
  about: "À propos",
  timeline: "Programme",
  rsvp: "RSVP",
  lastDay: "Dernier jour",
  countdownDays: "j",
  countdownHours: "h",
  countdownMinutes: "min",
  enjoyCelebration: "Profitez de la fête",
  openAppleMaps: "Ouvrir l’itinéraire dans Plans",
  openGoogleMaps: "Ouvrir l’itinéraire dans Google Maps",
  appleMaps: "Plans",
  googleMaps: "Google Maps",
  venueMapAria: "Carte du lieu",
  rsvpCapacityOne: "Cet événement accepte au plus 1 invité avec cette invitation.",
  rsvpCapacityMany: "Cet événement accepte au plus {n} invités avec cette invitation.",
  rsvpHelpText:
    "Veuillez saisir au moins un nom. Les autres lignes sont facultatives si vous venez seul(e).",
  guestNumber: "Invité {n}",
  fullNamePlaceholder: "Nom complet",
  optionalNamePlaceholder: "Facultatif — nom complet",
  mealPreferencesOptional: "Préférences repas (facultatif)",
  mealVegetarian: "Végétarien",
  mealVegan: "Végétalien",
  mealGlutenFree: "Sans gluten",
  mealNutAllergy: "Allergie aux fruits à coque",
  submitRsvp: "Envoyer le RSVP",
  rsvpNotEnabled: "Le RSVP n’est pas activé pour cette invitation.",
  rsvpDeadlinePassed: "La date limite de RSVP est passée.",
  rsvpNotReady:
    "Le RSVP n’est pas encore prêt. Les organisateurs doivent définir une limite d’invités dans l’app.",
  nameTooLong: "Le nom est trop long.",
  invalidData: "Données invalides.",
  enterGuestName: "Veuillez saisir au moins un nom d’invité.",
  rsvpThankYou: "Merci ! Votre RSVP a bien été reçu.",
  rsvpSubmitError: "Envoi impossible. Veuillez réessayer.",
  rsvpSaveChanges: "Enregistrer les modifications",
  rsvpWithdraw: "Supprimer mon RSVP",
  rsvpWithdrawDialogTitle: "Supprimer votre RSVP ?",
  rsvpWithdrawDialogBody:
    "Les hôtes ne verront plus cette réponse. Si la date limite le permet, vous pourrez envoyer un nouveau RSVP ensuite.",
  rsvpWithdrawConfirm: "Supprimer le RSVP",
  rsvpWithdrawCancel: "Annuler",
  rsvpWithdrawnToast: "Votre RSVP a été supprimé.",
  rsvpSessionNote:
    "Ce navigateur mémorise votre réponse (connexion anonyme). Si vous effacez les données du site ou utilisez un autre appareil, vous devrez peut‑être répondre à nouveau.",
  rsvpAuthError: "Impossible de démarrer la session. Actualisez la page.",
  appPromoText: "Téléchargez l’app iPhone pour créer et gérer vos invitations.",
  downloadAppStoreAria: "Télécharger sur l’App Store",
  dateTBD: "Date à confirmer",
  scheduleStarts: "Début",
  scheduleEnds: "Fin",
  addToCalendar: "Ajouter au calendrier",
  addToCalendarAria: "Télécharger un fichier calendrier (.ics) pour cette invitation",
  songRequestTitle: "Demandes de chansons",
  songRequestIntro:
    "Indiquez votre nom, puis remplissez jusqu’à cinq cases avec des liens de titres Spotify ou Apple Music — collez ou déposez le lien. Un aperçu court s’affiche lorsque le lien est reconnu.",
  songRequestRequesterLabel: "Votre nom",
  songRequestRequesterPlaceholder: "Qui fait la demande ?",
  songRequestLinkLabel: "Lien du morceau",
  songRequestLinkPlaceholder: "URL Spotify ou Apple Music",
  songRequestAddTrack: "Ajouter un autre titre",
  songRequestRemoveTrack: "Supprimer",
  songRequestPreviewLoading: "Recherche du morceau…",
  songRequestPreviewError: "Impossible de lire ce lien. Vérifiez qu’il s’agit d’un titre Spotify ou Apple Music.",
  songRequestInvalidLink: "Utilisez un lien de titre Spotify ou Apple Music.",
  songRequestLimitReached: "Vous pouvez ajouter au plus cinq titres.",
  songRequestSubmit: "Envoyer les demandes",
  songRequestThankYou: "Merci ! Vos demandes de chansons ont été envoyées.",
  songRequestSubmitError: "Envoi impossible. Veuillez réessayer.",
  songRequestNeedName: "Veuillez saisir votre nom.",
  songRequestNeedOneResolvedTrack: "Remplissez au moins une case et attendez l’aperçu.",
  songRequestSpotifyAria: "Spotify",
  songRequestAppleMusicAria: "Apple Music",
  songRequestSlotsCaption:
    "Touchez une case, puis collez (Ctrl+V / ⌘V) ou déposez un lien. Cinq emplacements — seuls ceux remplis sont envoyés.",
  songRequestSlotPasteHint: "Coller ou déposer",
  songRequestSlotAria:
    "Emplacement chanson {n}. Touchez, puis collez ou déposez un lien Spotify ou Apple Music.",
};

const de: Messages = {
  ...en,
  invitePageTitleFallbackWedding: "Hochzeit",
  metaTitle: "Einladung",
  loading: "Wird geladen…",
  invitationNotFound: "Einladung nicht gefunden",
  invalidInviteLink:
    "Bitte einen gültigen Einladungslink öffnen, z. B. /your-slug, /invite/your-slug oder ?invite=your-slug auf localhost.",
  invitationUnavailable: "Diese Einladung existiert nicht oder ist nicht verfügbar.",
  unknownError: "Unbekannter Fehler",
  noPhoto: "Kein Foto",
  noPhotoAria: "Kein Foto",
  location: "Ort",
  about: "Über uns",
  timeline: "Ablauf",
  rsvp: "Rückmeldung",
  lastDay: "Letzter Tag",
  countdownDays: "T",
  countdownHours: "Std",
  countdownMinutes: "Min",
  enjoyCelebration: "Feiern Sie schön",
  openAppleMaps: "Route in Apple Karten öffnen",
  openGoogleMaps: "Route in Google Maps öffnen",
  appleMaps: "Apple Karten",
  googleMaps: "Google Maps",
  venueMapAria: "Veranstaltungsort-Karte",
  rsvpCapacityOne: "Mit dieser Einladung sind bis zu 1 Gast möglich.",
  rsvpCapacityMany: "Mit dieser Einladung sind bis zu {n} Gäste möglich.",
  rsvpHelpText:
    "Bitte mindestens einen Namen eintragen. Weitere Zeilen sind optional, wenn Sie allein kommen.",
  guestNumber: "Gast {n}",
  fullNamePlaceholder: "Vollständiger Name",
  optionalNamePlaceholder: "Optional — vollständiger Name",
  mealPreferencesOptional: "Essenswünsche (optional)",
  mealVegetarian: "Vegetarisch",
  mealVegan: "Vegan",
  mealGlutenFree: "Glutenfrei",
  mealNutAllergy: "Nussallergie",
  submitRsvp: "Rückmeldung senden",
  rsvpNotEnabled: "Rückmeldung ist für diese Einladung nicht aktiviert.",
  rsvpDeadlinePassed: "Die Rückmeldefrist ist abgelaufen.",
  rsvpNotReady:
    "Rückmeldung ist noch nicht bereit. Die Gastgeber müssen in der App ein Gästelimit festlegen.",
  nameTooLong: "Der Name ist zu lang.",
  invalidData: "Ungültige Daten.",
  enterGuestName: "Bitte mindestens einen Gastnamen eingeben.",
  rsvpThankYou: "Danke! Ihre Rückmeldung ist eingegangen.",
  rsvpSubmitError: "Senden fehlgeschlagen. Bitte erneut versuchen.",
  rsvpSaveChanges: "Änderungen speichern",
  rsvpWithdraw: "Rückmeldung löschen",
  rsvpWithdrawDialogTitle: "Rückmeldung löschen?",
  rsvpWithdrawDialogBody:
    "Die Gastgeber sehen diese Antwort nicht mehr. Wenn die Frist es zulässt, können Sie später erneut eine Rückmeldung senden.",
  rsvpWithdrawConfirm: "Rückmeldung löschen",
  rsvpWithdrawCancel: "Abbrechen",
  rsvpWithdrawnToast: "Ihre Rückmeldung wurde entfernt.",
  rsvpSessionNote:
    "Dieser Browser speichert Ihre Antwort (anonyme Anmeldung). Nach Löschen der Website-Daten oder auf einem anderen Gerät müssen Sie ggf. erneut antworten.",
  rsvpAuthError: "Sitzung konnte nicht gestartet werden. Bitte Seite neu laden.",
  appPromoText: "Hol dir die iPhone-App, um Einladungen zu erstellen und zu verwalten.",
  downloadAppStoreAria: "Im App Store herunterladen",
  dateTBD: "Datum offen",
  scheduleStarts: "Beginn",
  scheduleEnds: "Ende",
  addToCalendar: "Zum Kalender hinzufügen",
  addToCalendarAria: "Kalenderdatei (.ics) für diese Einladung herunterladen",
  songRequestTitle: "Musikwünsche",
  songRequestIntro:
    "Geben Sie Ihren Namen ein und füllen Sie bis zu fünf Kästchen mit Spotify- oder Apple-Music-Songlinks — einfügen oder per Drag & Drop ablegen. Nach Erkennung erscheint eine kurze Vorschau.",
  songRequestRequesterLabel: "Ihr Name",
  songRequestRequesterPlaceholder: "Wer wünscht den Song?",
  songRequestLinkLabel: "Song-Link",
  songRequestLinkPlaceholder: "Spotify- oder Apple-Music-URL",
  songRequestAddTrack: "Weiteren Song hinzufügen",
  songRequestRemoveTrack: "Entfernen",
  songRequestPreviewLoading: "Titel wird gesucht…",
  songRequestPreviewError: "Link konnte nicht gelesen werden. Bitte Spotify- oder Apple-Music-Songlink prüfen.",
  songRequestInvalidLink: "Bitte verwenden Sie einen Spotify- oder Apple-Music-Songlink.",
  songRequestLimitReached: "Es sind höchstens fünf Songs möglich.",
  songRequestSubmit: "Wünsche senden",
  songRequestThankYou: "Danke! Ihre Musikwünsche wurden gesendet.",
  songRequestSubmitError: "Senden fehlgeschlagen. Bitte erneut versuchen.",
  songRequestNeedName: "Bitte geben Sie Ihren Namen ein.",
  songRequestNeedOneResolvedTrack: "Füllen Sie mindestens ein Kästchen und warten Sie auf die Vorschau.",
  songRequestSpotifyAria: "Spotify",
  songRequestAppleMusicAria: "Apple Music",
  songRequestSlotsCaption:
    "Tippen Sie auf ein Kästchen, dann einfügen (Strg+V / ⌘V) oder Link ablegen. Fünf Plätze — nur ausgefüllte werden gesendet.",
  songRequestSlotPasteHint: "Einfügen oder ablegen",
  songRequestSlotAria:
    "Song-Platz {n}. Tippen, dann Spotify- oder Apple-Music-Link einfügen oder ablegen.",
};

const es: Messages = {
  ...en,
  invitePageTitleFallbackWedding: "Boda",
  metaTitle: "Invitación",
  loading: "Cargando…",
  invitationNotFound: "No se encontró la invitación",
  invalidInviteLink:
    "Abre un enlace de invitación válido, p. ej. /tu-slug, /invite/tu-slug o ?invite=tu-slug en localhost.",
  invitationUnavailable: "Esta invitación no existe o no está disponible.",
  unknownError: "Error desconocido",
  noPhoto: "Sin foto",
  noPhotoAria: "Sin foto",
  location: "Ubicación",
  about: "Acerca de",
  timeline: "Programa",
  rsvp: "RSVP",
  lastDay: "Último día",
  countdownDays: "d",
  countdownHours: "h",
  countdownMinutes: "min",
  enjoyCelebration: "Disfruten la celebración",
  openAppleMaps: "Abrir indicaciones en Apple Maps",
  openGoogleMaps: "Abrir indicaciones en Google Maps",
  appleMaps: "Apple Maps",
  googleMaps: "Google Maps",
  venueMapAria: "Mapa del lugar",
  rsvpCapacityOne: "Este evento admite hasta 1 invitado con esta invitación.",
  rsvpCapacityMany: "Este evento admite hasta {n} invitados con esta invitación.",
  rsvpHelpText:
    "Introduce al menos un nombre. Las demás filas son opcionales si vienes solo/a.",
  guestNumber: "Invitado {n}",
  fullNamePlaceholder: "Nombre completo",
  optionalNamePlaceholder: "Opcional — nombre completo",
  mealPreferencesOptional: "Preferencias de menú (opcional)",
  mealVegetarian: "Vegetariano",
  mealVegan: "Vegano",
  mealGlutenFree: "Sin gluten",
  mealNutAllergy: "Alergia a frutos secos",
  submitRsvp: "Enviar RSVP",
  rsvpNotEnabled: "RSVP no está habilitado para esta invitación.",
  rsvpDeadlinePassed: "La fecha límite de RSVP ha pasado.",
  rsvpNotReady: "RSVP aún no está listo. Los anfitriones deben fijar un límite de invitados en la app.",
  nameTooLong: "El nombre es demasiado largo.",
  invalidData: "Datos no válidos.",
  enterGuestName: "Introduce al menos un nombre de invitado.",
  rsvpThankYou: "¡Gracias! Hemos recibido tu RSVP.",
  rsvpSubmitError: "No se pudo enviar. Inténtalo de nuevo.",
  rsvpSaveChanges: "Guardar cambios",
  rsvpWithdraw: "Eliminar mi RSVP",
  rsvpWithdrawDialogTitle: "¿Eliminar tu RSVP?",
  rsvpWithdrawDialogBody:
    "Los anfitriones ya no verán esta respuesta. Si la fecha límite lo permite, puedes enviar un RSVP nuevo después.",
  rsvpWithdrawConfirm: "Eliminar RSVP",
  rsvpWithdrawCancel: "Cancelar",
  rsvpWithdrawnToast: "Se eliminó tu RSVP.",
  rsvpSessionNote:
    "Este navegador recuerda tu respuesta (inicio de sesión anónimo). Si borras los datos del sitio o usas otro dispositivo, puede que debas responder de nuevo.",
  rsvpAuthError: "No se pudo iniciar la sesión. Actualiza la página.",
  appPromoText: "Descarga la app para iPhone para crear y gestionar tus invitaciones.",
  downloadAppStoreAria: "Descargar en el App Store",
  dateTBD: "Fecha por confirmar",
  scheduleStarts: "Comienza",
  scheduleEnds: "Termina",
  addToCalendar: "Añadir al calendario",
  addToCalendarAria: "Descargar archivo de calendario (.ics) para esta invitación",
  songRequestTitle: "Petición de canciones",
  songRequestIntro:
    "Escribe tu nombre y rellena hasta cinco casillas con enlaces de canciones de Spotify o Apple Music — pega o arrastra el enlace. Verás una vista previa cuando se reconozca el enlace.",
  songRequestRequesterLabel: "Tu nombre",
  songRequestRequesterPlaceholder: "¿Quién lo pide?",
  songRequestLinkLabel: "Enlace de la canción",
  songRequestLinkPlaceholder: "URL de Spotify o Apple Music",
  songRequestAddTrack: "Añadir otra canción",
  songRequestRemoveTrack: "Quitar",
  songRequestPreviewLoading: "Buscando la canción…",
  songRequestPreviewError: "No se pudo leer el enlace. Comprueba que sea una canción de Spotify o Apple Music.",
  songRequestInvalidLink: "Usa solo enlaces de canción de Spotify o Apple Music.",
  songRequestLimitReached: "Como máximo puedes añadir cinco canciones.",
  songRequestSubmit: "Enviar peticiones",
  songRequestThankYou: "¡Gracias! Tus peticiones de canciones se han enviado.",
  songRequestSubmitError: "No se pudo enviar. Inténtalo de nuevo.",
  songRequestNeedName: "Introduce tu nombre.",
  songRequestNeedOneResolvedTrack: "Rellena al menos una casilla y espera a que aparezca la vista previa.",
  songRequestSpotifyAria: "Spotify",
  songRequestAppleMusicAria: "Apple Music",
  songRequestSlotsCaption:
    "Toca una casilla, pega (Ctrl+V / ⌘V) o suelta un enlace. Cinco huecos — solo se envían los que tengan contenido.",
  songRequestSlotPasteHint: "Pegar o soltar",
  songRequestSlotAria: "Casilla de canción {n}. Toca; pega o suelta un enlace de Spotify o Apple Music.",
};

const it: Messages = {
  ...en,
  invitePageTitleFallbackWedding: "Matrimonio",
  metaTitle: "Invito",
  loading: "Caricamento…",
  invitationNotFound: "Invito non trovato",
  invalidInviteLink:
    "Apri un link d’invito valido, ad es. /tuo-slug, /invite/tuo-slug o ?invite=tuo-slug su localhost.",
  invitationUnavailable: "Questo invito non esiste o non è disponibile.",
  unknownError: "Errore sconosciuto",
  noPhoto: "Nessuna foto",
  noPhotoAria: "Nessuna foto",
  location: "Luogo",
  about: "Informazioni",
  timeline: "Programma",
  rsvp: "RSVP",
  lastDay: "Ultimo giorno",
  countdownDays: "g",
  countdownHours: "h",
  countdownMinutes: "min",
  enjoyCelebration: "Buona festa",
  openAppleMaps: "Apri indicazioni in Mappe",
  openGoogleMaps: "Apri indicazioni in Google Maps",
  appleMaps: "Mappe",
  googleMaps: "Google Maps",
  venueMapAria: "Mappa del luogo",
  rsvpCapacityOne: "Con questo invito l’evento accetta al massimo 1 ospite.",
  rsvpCapacityMany: "Con questo invito l’evento accetta al massimo {n} ospiti.",
  rsvpHelpText:
    "Inserisci almeno un nome. Le altre righe sono facoltative se partecipi da solo/a.",
  guestNumber: "Ospite {n}",
  fullNamePlaceholder: "Nome e cognome",
  optionalNamePlaceholder: "Facoltativo — nome e cognome",
  mealPreferencesOptional: "Preferenze pasto (facoltativo)",
  mealVegetarian: "Vegetariano",
  mealVegan: "Vegano",
  mealGlutenFree: "Senza glutine",
  mealNutAllergy: "Allergia alle noci",
  submitRsvp: "Invia RSVP",
  rsvpNotEnabled: "RSVP non abilitato per questo invito.",
  rsvpDeadlinePassed: "Il termine RSVP è scaduto.",
  rsvpNotReady: "RSVP non è ancora pronto. Gli host devono impostare un limite ospiti nell’app.",
  nameTooLong: "Il nome è troppo lungo.",
  invalidData: "Dati non validi.",
  enterGuestName: "Inserisci almeno un nome ospite.",
  rsvpThankYou: "Grazie! RSVP ricevuto.",
  rsvpSubmitError: "Invio non riuscito. Riprova.",
  rsvpSaveChanges: "Salva modifiche",
  rsvpWithdraw: "Elimina il mio RSVP",
  rsvpWithdrawDialogTitle: "Eliminare il RSVP?",
  rsvpWithdrawDialogBody:
    "Gli host non vedranno più questa risposta. Se la scadenza lo consente, potrai inviare un nuovo RSVP in seguito.",
  rsvpWithdrawConfirm: "Elimina RSVP",
  rsvpWithdrawCancel: "Annulla",
  rsvpWithdrawnToast: "Il RSVP è stato rimosso.",
  rsvpSessionNote:
    "Questo browser memorizza la risposta (accesso anonimo). Cancellando i dati del sito o su un altro dispositivo potrebbe essere necessario rispondere di nuovo.",
  rsvpAuthError: "Impossibile avviare la sessione. Aggiorna la pagina.",
  appPromoText: "Scarica l’app per iPhone per creare e gestire i tuoi inviti.",
  downloadAppStoreAria: "Scarica dall’App Store",
  dateTBD: "Data da definire",
  scheduleStarts: "Inizio",
  scheduleEnds: "Fine",
  addToCalendar: "Aggiungi al calendario",
  addToCalendarAria: "Scarica un file di calendario (.ics) per questo invito",
  songRequestTitle: "Richieste musicali",
  songRequestIntro:
    "Scrivi il tuo nome e riempi fino a cinque riquadri con link a brani Spotify o Apple Music — incolla o trascina il link. Compare un’anteprima quando il link viene riconosciuto.",
  songRequestRequesterLabel: "Il tuo nome",
  songRequestRequesterPlaceholder: "Chi richiede?",
  songRequestLinkLabel: "Link del brano",
  songRequestLinkPlaceholder: "URL Spotify o Apple Music",
  songRequestAddTrack: "Aggiungi un altro brano",
  songRequestRemoveTrack: "Rimuovi",
  songRequestPreviewLoading: "Ricerca del brano…",
  songRequestPreviewError: "Impossibile leggere il link. Verifica che sia un brano Spotify o Apple Music.",
  songRequestInvalidLink: "Usa un link a un brano Spotify o Apple Music.",
  songRequestLimitReached: "Puoi aggiungere al massimo cinque brani.",
  songRequestSubmit: "Invia richieste",
  songRequestThankYou: "Grazie! Le richieste musicali sono state inviate.",
  songRequestSubmitError: "Invio non riuscito. Riprova.",
  songRequestNeedName: "Inserisci il tuo nome.",
  songRequestNeedOneResolvedTrack: "Compila almeno un riquadro e attendi l’anteprima.",
  songRequestSpotifyAria: "Spotify",
  songRequestAppleMusicAria: "Apple Music",
  songRequestSlotsCaption:
    "Tocca un riquadro, poi incolla (Ctrl+V / ⌘V) o rilascia un link. Cinque slot — vengono inviati solo quelli compilati.",
  songRequestSlotPasteHint: "Incolla o rilascia",
  songRequestSlotAria:
    "Slot brano {n}. Tocca; poi incolla o rilascia un link Spotify o Apple Music.",
};

const ptPT: Messages = {
  ...en,
  invitePageTitleFallbackWedding: "Casamento",
  metaTitle: "Convite",
  loading: "A carregar…",
  invitationNotFound: "Convite não encontrado",
  invalidInviteLink:
    "Abra uma ligação de convite válida, ex.: /seu-slug, /invite/seu-slug ou ?invite=seu-slug em localhost.",
  invitationUnavailable: "Este convite não existe ou não está disponível.",
  unknownError: "Erro desconhecido",
  noPhoto: "Sem fotografia",
  noPhotoAria: "Sem fotografia",
  location: "Local",
  about: "Sobre",
  timeline: "Programa",
  rsvp: "RSVP",
  lastDay: "Último dia",
  countdownDays: "d",
  countdownHours: "h",
  countdownMinutes: "min",
  enjoyCelebration: "Desfrutem da festa",
  openAppleMaps: "Abrir direções no Apple Maps",
  openGoogleMaps: "Abrir direções no Google Maps",
  appleMaps: "Apple Maps",
  googleMaps: "Google Maps",
  venueMapAria: "Mapa do local",
  rsvpCapacityOne: "Este evento aceita no máximo 1 convidado com este convite.",
  rsvpCapacityMany: "Este evento aceita no máximo {n} convidados com este convite.",
  rsvpHelpText:
    "Introduza pelo menos um nome. As outras linhas são opcionais se vier sozinho/a.",
  guestNumber: "Convidado {n}",
  fullNamePlaceholder: "Nome completo",
  optionalNamePlaceholder: "Opcional — nome completo",
  mealPreferencesOptional: "Preferências de refeição (opcional)",
  mealVegetarian: "Vegetariano",
  mealVegan: "Vegano",
  mealGlutenFree: "Sem glúten",
  mealNutAllergy: "Alergia a frutos secos",
  submitRsvp: "Enviar RSVP",
  rsvpNotEnabled: "RSVP não está ativo para este convite.",
  rsvpDeadlinePassed: "O prazo de RSVP terminou.",
  rsvpNotReady: "RSVP ainda não está pronto. Os anfitriões devem definir um limite na aplicação.",
  nameTooLong: "O nome é demasiado longo.",
  invalidData: "Dados inválidos.",
  enterGuestName: "Introduza pelo menos um nome de convidado.",
  rsvpThankYou: "Obrigado! Recebemos o seu RSVP.",
  rsvpSubmitError: "Não foi possível enviar. Tente novamente.",
  rsvpSaveChanges: "Guardar alterações",
  rsvpWithdraw: "Apagar o meu RSVP",
  rsvpWithdrawDialogTitle: "Apagar o seu RSVP?",
  rsvpWithdrawDialogBody:
    "Os anfitriões deixarão de ver esta resposta. Se o prazo o permitir, poderá enviar um novo RSVP mais tarde.",
  rsvpWithdrawConfirm: "Apagar RSVP",
  rsvpWithdrawCancel: "Cancelar",
  rsvpWithdrawnToast: "O seu RSVP foi removido.",
  rsvpSessionNote:
    "Este navegador memoriza a sua resposta (início de sessão anónimo). Se limpar os dados do site ou usar outro dispositivo, poderá ter de responder novamente.",
  rsvpAuthError: "Não foi possível iniciar a sessão. Atualize a página.",
  appPromoText: "Transfira a app para iPhone para criar e gerir os seus convites.",
  downloadAppStoreAria: "Transferir na App Store",
  dateTBD: "Data por definir",
  scheduleStarts: "Início",
  scheduleEnds: "Fim",
  addToCalendar: "Adicionar ao calendário",
  addToCalendarAria: "Transferir ficheiro de calendário (.ics) para este convite",
  songRequestTitle: "Pedidos de música",
  songRequestIntro:
    "Escreva o seu nome e preencha até cinco áreas com ligações a músicas Spotify ou Apple Music — cole ou largue a ligação. É mostrada uma pré-visualização quando a ligação é reconhecida.",
  songRequestRequesterLabel: "O seu nome",
  songRequestRequesterPlaceholder: "Quem pede?",
  songRequestLinkLabel: "Link da música",
  songRequestLinkPlaceholder: "URL Spotify ou Apple Music",
  songRequestAddTrack: "Adicionar outra música",
  songRequestRemoveTrack: "Remover",
  songRequestPreviewLoading: "A procurar a música…",
  songRequestPreviewError: "Não foi possível ler a ligação. Confirme que é uma música Spotify ou Apple Music.",
  songRequestInvalidLink: "Use apenas links de músicas Spotify ou Apple Music.",
  songRequestLimitReached: "Pode adicionar no máximo cinco músicas.",
  songRequestSubmit: "Enviar pedidos",
  songRequestThankYou: "Obrigado! Os seus pedidos de música foram enviados.",
  songRequestSubmitError: "Não foi possível enviar. Tente novamente.",
  songRequestNeedName: "Introduza o seu nome.",
  songRequestNeedOneResolvedTrack: "Preencha pelo menos uma área e aguarde a pré-visualização.",
  songRequestSpotifyAria: "Spotify",
  songRequestAppleMusicAria: "Apple Music",
  songRequestSlotsCaption:
    "Toque numa área, depois cole (Ctrl+V / ⌘V) ou largue uma ligação. Cinco espaços — só os preenchidos são enviados.",
  songRequestSlotPasteHint: "Colar ou largar",
  songRequestSlotAria: "Espaço de música {n}. Toque; cole ou largue uma ligação Spotify ou Apple Music.",
};

const ru: Messages = {
  ...en,
  invitePageTitleFallbackWedding: "Свадьба",
  metaTitle: "Приглашение",
  loading: "Загрузка…",
  invitationNotFound: "Приглашение не найдено",
  invalidInviteLink:
    "Откройте действительную ссылку, напр. /your-slug, /invite/your-slug или ?invite=your-slug на localhost.",
  invitationUnavailable: "Это приглашение не существует или недоступно.",
  unknownError: "Неизвестная ошибка",
  noPhoto: "Нет фото",
  noPhotoAria: "Нет фото",
  location: "Место",
  about: "О мероприятии",
  timeline: "Программа",
  rsvp: "Ответ",
  lastDay: "Последний день",
  countdownDays: "д",
  countdownHours: "ч",
  countdownMinutes: "мин",
  enjoyCelebration: "Приятного праздника",
  openAppleMaps: "Открыть маршрут в Apple Карты",
  openGoogleMaps: "Открыть маршрут в Google Картах",
  appleMaps: "Apple Карты",
  googleMaps: "Google Карты",
  venueMapAria: "Карта места",
  rsvpCapacityOne: "По этому приглашению принимается не более 1 гостя.",
  rsvpCapacityMany: "По этому приглашению принимается не более {n} гостей.",
  rsvpHelpText:
    "Укажите хотя бы одно имя. Остальные строки необязательны, если вы приходите один/одна.",
  guestNumber: "Гость {n}",
  fullNamePlaceholder: "Полное имя",
  optionalNamePlaceholder: "Необязательно — полное имя",
  mealPreferencesOptional: "Предпочтения по еде (необязательно)",
  mealVegetarian: "Вегетарианское",
  mealVegan: "Веганское",
  mealGlutenFree: "Без глютена",
  mealNutAllergy: "Аллергия на орехи",
  submitRsvp: "Отправить ответ",
  rsvpNotEnabled: "Ответ по этому приглашению отключён.",
  rsvpDeadlinePassed: "Срок ответа истёк.",
  rsvpNotReady: "Ответ ещё не настроен. Организаторы должны указать лимит гостей в приложении.",
  nameTooLong: "Слишком длинное имя.",
  invalidData: "Неверные данные.",
  enterGuestName: "Введите имя хотя бы одного гостя.",
  rsvpThankYou: "Спасибо! Мы получили ваш ответ.",
  rsvpSubmitError: "Не удалось отправить. Попробуйте снова.",
  rsvpSaveChanges: "Сохранить изменения",
  rsvpWithdraw: "Удалить мой ответ",
  rsvpWithdrawDialogTitle: "Удалить ответ?",
  rsvpWithdrawDialogBody:
    "Организаторы больше не увидят этот ответ. Если срок позволяет, позже можно отправить новый ответ.",
  rsvpWithdrawConfirm: "Удалить ответ",
  rsvpWithdrawCancel: "Отмена",
  rsvpWithdrawnToast: "Ваш ответ удалён.",
  rsvpSessionNote:
    "Этот браузер запоминает ответ (анонимный вход). Если очистить данные сайта или зайти с другого устройства, возможно, придётся ответить снова.",
  rsvpAuthError: "Не удалось начать сессию. Обновите страницу.",
  appPromoText: "Скачайте приложение для iPhone, чтобы создавать и управлять приглашениями.",
  downloadAppStoreAria: "Загрузить в App Store",
  dateTBD: "Дата уточняется",
  scheduleStarts: "Начало",
  scheduleEnds: "Конец",
  addToCalendar: "Добавить в календарь",
  addToCalendarAria: "Скачать файл календаря (.ics) для этого приглашения",
  songRequestTitle: "Музыкальные пожелания",
  songRequestIntro:
    "Укажите имя и заполните до пяти ячеек ссылками на песни Spotify или Apple Music — вставьте ссылку или перетащите её. После распознавания показывается короткий предпросмотр.",
  songRequestRequesterLabel: "Ваше имя",
  songRequestRequesterPlaceholder: "Кто заказывает?",
  songRequestLinkLabel: "Ссылка на трек",
  songRequestLinkPlaceholder: "URL Spotify или Apple Music",
  songRequestAddTrack: "Добавить ещё песню",
  songRequestRemoveTrack: "Удалить",
  songRequestPreviewLoading: "Поиск трека…",
  songRequestPreviewError: "Не удалось прочитать ссылку. Проверьте, что это трек Spotify или Apple Music.",
  songRequestInvalidLink: "Используйте ссылку на трек Spotify или Apple Music.",
  songRequestLimitReached: "Можно добавить не больше пяти песен.",
  songRequestSubmit: "Отправить пожелания",
  songRequestThankYou: "Спасибо! Ваши пожелания отправлены.",
  songRequestSubmitError: "Не удалось отправить. Попробуйте снова.",
  songRequestNeedName: "Введите имя.",
  songRequestNeedOneResolvedTrack: "Заполните хотя бы одну ячейку и дождитесь предпросмотра.",
  songRequestSpotifyAria: "Spotify",
  songRequestAppleMusicAria: "Apple Music",
  songRequestSlotsCaption:
    "Нажмите на ячейку, затем вставьте (Ctrl+V / ⌘V) или перетащите ссылку. Пять слотов — отправляются только заполненные.",
  songRequestSlotPasteHint: "Вставить или сбросить",
  songRequestSlotAria: "Ячейка песни {n}. Нажмите; вставьте или перетащите ссылку Spotify или Apple Music.",
};

const ja: Messages = {
  ...en,
  invitePageTitleFallbackWedding: "結婚式",
  metaTitle: "招待状",
  loading: "読み込み中…",
  invitationNotFound: "招待状が見つかりません",
  invalidInviteLink:
    "有効な招待リンクを開いてください（例：/your-slug、/invite/your-slug、localhost では ?invite=your-slug）。",
  invitationUnavailable: "この招待状は存在しないか、利用できません。",
  unknownError: "不明なエラー",
  noPhoto: "写真なし",
  noPhotoAria: "写真なし",
  location: "場所",
  about: "概要",
  timeline: "タイムライン",
  rsvp: "出欠回答",
  lastDay: "最終日",
  countdownDays: "日",
  countdownHours: "時間",
  countdownMinutes: "分",
  enjoyCelebration: "お楽しみください",
  openAppleMaps: "Apple マップで経路を開く",
  openGoogleMaps: "Google マップで経路を開く",
  appleMaps: "Apple マップ",
  googleMaps: "Google マップ",
  venueMapAria: "会場マップ",
  rsvpCapacityOne: "この招待状では最大 1 名までです。",
  rsvpCapacityMany: "この招待状では最大 {n} 名までです。",
  rsvpHelpText:
    "少なくとも 1 名分のお名前を入力してください。お一人の場合は他の行は任意です。",
  guestNumber: "ゲスト {n}",
  fullNamePlaceholder: "氏名",
  optionalNamePlaceholder: "任意 — 氏名",
  mealPreferencesOptional: "食事の希望（任意）",
  mealVegetarian: "ベジタリアン",
  mealVegan: "ビーガン",
  mealGlutenFree: "グルテンフリー",
  mealNutAllergy: "ナッツアレルギー",
  submitRsvp: "出欠を送信",
  rsvpNotEnabled: "この招待状では出欠回答は利用できません。",
  rsvpDeadlinePassed: "出欠回答の締め切りが過ぎました。",
  rsvpNotReady: "出欠回答の準備ができていません。ホストがアプリで人数上限を設定する必要があります。",
  nameTooLong: "名前が長すぎます。",
  invalidData: "データが無効です。",
  enterGuestName: "少なくとも 1 名のゲスト名を入力してください。",
  rsvpThankYou: "ありがとうございます。出欠を受け付けました。",
  rsvpSubmitError: "送信できませんでした。もう一度お試しください。",
  rsvpSaveChanges: "変更を保存",
  rsvpWithdraw: "出欠を削除",
  rsvpWithdrawDialogTitle: "出欠を削除しますか？",
  rsvpWithdrawDialogBody:
    "ホスト側にはこの回答が表示されなくなります。期限内であれば、あとから新しく出欠を送信できます。",
  rsvpWithdrawConfirm: "削除する",
  rsvpWithdrawCancel: "キャンセル",
  rsvpWithdrawnToast: "出欠を削除しました。",
  rsvpSessionNote:
    "このブラウザは回答を記憶します（匿名ログイン）。サイトデータを消したり別端末を使うと、再度回答が必要になる場合があります。",
  rsvpAuthError: "セッションを開始できませんでした。ページを更新してください。",
  appPromoText: "iPhone アプリで招待状の作成と管理ができます。",
  downloadAppStoreAria: "App Store からダウンロード",
  dateTBD: "日程未定",
  scheduleStarts: "開始",
  scheduleEnds: "終了",
  addToCalendar: "カレンダーに追加",
  addToCalendarAria: "この招待のカレンダーファイル（.ics）をダウンロード",
  songRequestTitle: "曲リクエスト",
  songRequestIntro:
    "お名前を入力し、最大 5 つのマスに Spotify または Apple Music の曲リンクを貼り付けるかドロップしてください。リンクが認識されると短いプレビューが表示されます。",
  songRequestRequesterLabel: "お名前",
  songRequestRequesterPlaceholder: "リクエストする方のお名前",
  songRequestLinkLabel: "トラックのリンク",
  songRequestLinkPlaceholder: "Spotify または Apple Music の URL",
  songRequestAddTrack: "曲を追加",
  songRequestRemoveTrack: "削除",
  songRequestPreviewLoading: "曲を検索しています…",
  songRequestPreviewError:
    "リンクを読み取れませんでした。Spotify または Apple Music の曲リンクかご確認ください。",
  songRequestInvalidLink: "Spotify または Apple Music の曲リンクをご利用ください。",
  songRequestLimitReached: "追加できるのは最大 5 曲です。",
  songRequestSubmit: "リクエストを送信",
  songRequestThankYou: "ありがとうございます。曲リクエストを送信しました。",
  songRequestSubmitError: "送信できませんでした。もう一度お試しください。",
  songRequestNeedName: "お名前を入力してください。",
  songRequestNeedOneResolvedTrack: "少なくとも 1 つのマスを埋め、プレビューが表示されるまでお待ちください。",
  songRequestSpotifyAria: "Spotify",
  songRequestAppleMusicAria: "Apple Music",
  songRequestSlotsCaption:
    "マスをタップし、貼り付け（Ctrl+V / ⌘V）またはリンクをドロップ。5 つのスロット — 入っているものだけ送信されます。",
  songRequestSlotPasteHint: "貼り付けまたはドロップ",
  songRequestSlotAria: "曲スロット {n}。タップして Spotify または Apple Music のリンクを貼り付けまたはドロップ。",
};

const zhHans: Messages = {
  ...en,
  invitePageTitleFallbackWedding: "婚礼",
  metaTitle: "请柬",
  loading: "加载中…",
  invitationNotFound: "未找到请柬",
  invalidInviteLink: "请打开有效的邀请链接，例如 /your-slug、/invite/your-slug 或 localhost 上的 ?invite=your-slug。",
  invitationUnavailable: "此请柬不存在或不可用。",
  unknownError: "未知错误",
  noPhoto: "无照片",
  noPhotoAria: "无照片",
  location: "地点",
  about: "简介",
  timeline: "流程",
  rsvp: "回复邀请",
  lastDay: "截止日",
  countdownDays: "天",
  countdownHours: "小时",
  countdownMinutes: "分",
  enjoyCelebration: "祝您愉快",
  openAppleMaps: "在 Apple 地图中打开路线",
  openGoogleMaps: "在 Google 地图中打开路线",
  appleMaps: "Apple 地图",
  googleMaps: "Google 地图",
  venueMapAria: "场地地图",
  rsvpCapacityOne: "本请柬最多可容纳 1 位宾客。",
  rsvpCapacityMany: "本请柬最多可容纳 {n} 位宾客。",
  rsvpHelpText: "请至少填写一个姓名。若仅本人出席，其余行可选填。",
  guestNumber: "宾客 {n}",
  fullNamePlaceholder: "全名",
  optionalNamePlaceholder: "可选 — 全名",
  mealPreferencesOptional: "餐饮偏好（可选）",
  mealVegetarian: "素食",
  mealVegan: "纯素",
  mealGlutenFree: "无麸质",
  mealNutAllergy: "坚果过敏",
  submitRsvp: "提交回复",
  rsvpNotEnabled: "此请柬未开启回复邀请。",
  rsvpDeadlinePassed: "回复邀请已截止。",
  rsvpNotReady: "暂无法回复邀请。主办方需在 App 中设置人数上限。",
  nameTooLong: "姓名过长。",
  invalidData: "数据无效。",
  enterGuestName: "请至少输入一位宾客姓名。",
  rsvpThankYou: "谢谢！我们已收到您的回复。",
  rsvpSubmitError: "提交失败，请重试。",
  rsvpSaveChanges: "保存更改",
  rsvpWithdraw: "删除我的回复",
  rsvpWithdrawDialogTitle: "删除回复？",
  rsvpWithdrawDialogBody: "主办方将不再看到此回复。若仍在截止日期内，您可稍后提交新的回复。",
  rsvpWithdrawConfirm: "删除回复",
  rsvpWithdrawCancel: "取消",
  rsvpWithdrawnToast: "已删除您的回复。",
  rsvpSessionNote:
    "本浏览器会记住您的回复（匿名登录）。清除网站数据或使用其他设备后，可能需要重新回复。",
  rsvpAuthError: "无法开始会话，请刷新页面。",
  appPromoText: "下载 iPhone 应用以创建并管理您的请柬。",
  downloadAppStoreAria: "在 App Store 下载",
  dateTBD: "日期待定",
  scheduleStarts: "开始",
  scheduleEnds: "结束",
  addToCalendar: "添加到日历",
  addToCalendarAria: "下载此请柬的日历文件（.ics）",
  songRequestTitle: "点歌请求",
  songRequestIntro:
    "请先填写姓名，再在最多五个方框中粘贴或拖入 Spotify 或 Apple Music 歌曲链接。链接识别后将显示简短预览。",
  songRequestRequesterLabel: "您的姓名",
  songRequestRequesterPlaceholder: "谁是点歌人？",
  songRequestLinkLabel: "歌曲链接",
  songRequestLinkPlaceholder: "Spotify 或 Apple Music 链接",
  songRequestAddTrack: "再添加一首",
  songRequestRemoveTrack: "移除",
  songRequestPreviewLoading: "正在查找曲目…",
  songRequestPreviewError: "无法读取链接。请确认是 Spotify 或 Apple Music 的歌曲链接。",
  songRequestInvalidLink: "请使用 Spotify 或 Apple Music 歌曲链接。",
  songRequestLimitReached: "最多添加 5 首歌。",
  songRequestSubmit: "发送请求",
  songRequestThankYou: "谢谢！您的点歌请求已发送。",
  songRequestSubmitError: "发送失败，请重试。",
  songRequestNeedName: "请输入姓名。",
  songRequestNeedOneResolvedTrack: "请至少填满一个方框并等待预览出现。",
  songRequestSpotifyAria: "Spotify",
  songRequestAppleMusicAria: "Apple Music",
  songRequestSlotsCaption:
    "点击方框，然后粘贴（Ctrl+V / ⌘V）或拖入链接。五个歌曲位 — 仅发送已填写的。",
  songRequestSlotPasteHint: "粘贴或拖入",
  songRequestSlotAria: "歌曲位 {n}。点击后粘贴或拖入 Spotify 或 Apple Music 链接。",
};

const hi: Messages = {
  ...en,
  invitePageTitleFallbackWedding: "शादी",
  metaTitle: "निमंत्रण",
  loading: "लोड हो रहा है…",
  invitationNotFound: "आमंत्रण नहीं मिला",
  invalidInviteLink:
    "वैध आमंत्रण लिंक खोलें, उदा. /your-slug, /invite/your-slug या localhost पर ?invite=your-slug",
  invitationUnavailable: "यह आमंत्रण मौजूद नहीं है या उपलब्ध नहीं है।",
  unknownError: "अज्ञात त्रुटि",
  noPhoto: "कोई फ़ोटो नहीं",
  noPhotoAria: "कोई फ़ोटो नहीं",
  location: "स्थान",
  about: "जानकारी",
  timeline: "समय सारणी",
  rsvp: "आरएसवीपी",
  lastDay: "अंतिम दिन",
  countdownDays: "दि",
  countdownHours: "घं",
  countdownMinutes: "मि",
  enjoyCelebration: "आनंद लें",
  openAppleMaps: "Apple मानचित्र में दिशा खोलें",
  openGoogleMaps: "Google मानचित्र में दिशा खोलें",
  appleMaps: "Apple मानचित्र",
  googleMaps: "Google मानचित्र",
  venueMapAria: "स्थान मानचित्र",
  rsvpCapacityOne: "इस निमंत्रण पर अधिकतम 1 अतिथि स्वीकार है।",
  rsvpCapacityMany: "इस निमंत्रण पर अधिकतम {n} अतिथि स्वीकार हैं।",
  rsvpHelpText:
    "कृपया कम से कम एक नाम दर्ज करें। यदि आप अकेले आ रहे हैं तो अन्य पंक्तियाँ वैकल्पिक हैं।",
  guestNumber: "अतिथि {n}",
  fullNamePlaceholder: "पूरा नाम",
  optionalNamePlaceholder: "वैकल्पिक — पूरा नाम",
  mealPreferencesOptional: "भोजन वरीयताएँ (वैकल्पिक)",
  mealVegetarian: "शाकाहारी",
  mealVegan: "वीगन",
  mealGlutenFree: "ग्लूटेन मुक्त",
  mealNutAllergy: "मेवे से एलर्जी",
  submitRsvp: "आरएसवीपी भेजें",
  rsvpNotEnabled: "इस निमंत्रण के लिए आरएसवीपी सक्षम नहीं है।",
  rsvpDeadlinePassed: "आरएसवीपी की अंतिम तिथि बीत चुकी है।",
  rsvpNotReady: "आरएसवीपी अभी तैयार नहीं है। मेज़बानों को ऐप में अतिथि सीमा निर्धारित करनी होगी।",
  nameTooLong: "नाम बहुत लंबा है।",
  invalidData: "अमान्य डेटा।",
  enterGuestName: "कृपया कम से कम एक अतिथि का नाम दर्ज करें।",
  rsvpThankYou: "धन्यवाद! हमें आपका आरएसवीपी मिल गया।",
  rsvpSubmitError: "भेज नहीं सके। कृपया पुनः प्रयास करें।",
  rsvpSaveChanges: "बदलाव सहेजें",
  rsvpWithdraw: "मेरा RSVP हटाएँ",
  rsvpWithdrawDialogTitle: "अपना RSVP हटाएँ?",
  rsvpWithdrawDialogBody:
    "मेज़बान अब यह उत्तर नहीं देखेंगे। यदि समय सीमा अनुमति दे तो बाद में नया RSVP भेज सकते हैं।",
  rsvpWithdrawConfirm: "RSVP हटाएँ",
  rsvpWithdrawCancel: "रद्द करें",
  rsvpWithdrawnToast: "आपका RSVP हटा दिया गया।",
  rsvpSessionNote:
    "यह ब्राउज़र आपका उत्तर याद रखता है (अनाम साइन-इन)। साइट डेटा मिटाने या दूसरे डिवाइस पर आपको फिर उत्तर देना पड़ सकता है।",
  rsvpAuthError: "सत्र शुरू नहीं हो सका। पृष्ठ ताज़ा करें।",
  appPromoText: "अपने निमंत्रण बनाने और प्रबंधित करने के लिए iPhone ऐप लें।",
  downloadAppStoreAria: "App Store पर डाउनलोड करें",
  dateTBD: "तिथि निर्धारित नहीं",
  scheduleStarts: "प्रारंभ",
  scheduleEnds: "समाप्ति",
  addToCalendar: "कैलेंडर में जोड़ें",
  addToCalendarAria: "इस निमंत्रण के लिए कैलेंडर फ़ाइล (.ics) डाउनलोड करें",
  songRequestTitle: "गाने की फरमाइश",
  songRequestIntro:
    "अपना नाम लिखें, फिर अधिकतम पाँच खानों में Spotify या Apple Music गाने के लिंक चिपकाएँ या छोड़ें। लिंक पहचानने पर छोटा पूर्वावलोकन दिखता है।",
  songRequestRequesterLabel: "आपका नाम",
  songRequestRequesterPlaceholder: "कौन अनुरोध कर रहा है?",
  songRequestLinkLabel: "ट्रैक लिंक",
  songRequestLinkPlaceholder: "Spotify या Apple Music URL",
  songRequestAddTrack: "और गाना जोड़ें",
  songRequestRemoveTrack: "हटाएँ",
  songRequestPreviewLoading: "ट्रैक खोज रहे हैं…",
  songRequestPreviewError: "लिंक पढ़ा नहीं जा सका। कृपया पुष्टि करें कि यह Spotify या Apple Music गाना लिंक है।",
  songRequestInvalidLink: "कृपया Spotify या Apple Music गाना लिंक ही उपयोग करें।",
  songRequestLimitReached: "अधिकतम पाँच गाने जोड़ सकते हैं।",
  songRequestSubmit: "अनुरोध भेजें",
  songRequestThankYou: "धन्यवाद! आपके गाने के अनुरोध भेज दिए गए।",
  songRequestSubmitError: "नहीं भेज सके। कृपया फिर कोशिश करें।",
  songRequestNeedName: "कृपया अपना नाम दर्ज करें।",
  songRequestNeedOneResolvedTrack: "कम से कम एक खाना भरें और पूर्वावलोकन आने तक प्रतीक्षा करें।",
  songRequestSpotifyAria: "Spotify",
  songRequestAppleMusicAria: "Apple Music",
  songRequestSlotsCaption:
    "एक खाने को छुएँ, फिर चिपकाएँ (Ctrl+V / ⌘V) या लिंक छोड़ें। पाँच स्लॉट — केवल भरे हुए भेजे जाते हैं।",
  songRequestSlotPasteHint: "चिपकाएँ या छोड़ें",
  songRequestSlotAria: "गाना स्लॉट {n}। छुएँ; Spotify या Apple Music लिंक चिपकाएँ या छोड़ें।",
};

const id: Messages = {
  ...en,
  invitePageTitleFallbackWedding: "Pernikahan",
  metaTitle: "Undangan",
  loading: "Memuat…",
  invitationNotFound: "Undangan tidak ditemukan",
  invalidInviteLink:
    "Buka tautan undangan yang valid, mis. /your-slug, /invite/your-slug, atau ?invite=your-slug di localhost.",
  invitationUnavailable: "Undangan ini tidak ada atau tidak tersedia.",
  unknownError: "Kesalahan tidak diketahui",
  noPhoto: "Tidak ada foto",
  noPhotoAria: "Tidak ada foto",
  location: "Lokasi",
  about: "Tentang",
  timeline: "Jadwal",
  rsvp: "RSVP",
  lastDay: "Hari terakhir",
  countdownDays: "h",
  countdownHours: "j",
  countdownMinutes: "m",
  enjoyCelebration: "Selamat bersenang-senang",
  openAppleMaps: "Buka rute di Apple Maps",
  openGoogleMaps: "Buka rute di Google Maps",
  appleMaps: "Apple Maps",
  googleMaps: "Google Maps",
  venueMapAria: "Peta lokasi",
  rsvpCapacityOne: "Acara ini menerima maksimal 1 tamu dengan undangan ini.",
  rsvpCapacityMany: "Acara ini menerima maksimal {n} tamu dengan undangan ini.",
  rsvpHelpText:
    "Masukkan setidaknya satu nama. Baris lain opsional jika Anda datang sendiri.",
  guestNumber: "Tamu {n}",
  fullNamePlaceholder: "Nama lengkap",
  optionalNamePlaceholder: "Opsional — nama lengkap",
  mealPreferencesOptional: "Preferensi makanan (opsional)",
  mealVegetarian: "Vegetarian",
  mealVegan: "Vegan",
  mealGlutenFree: "Bebas gluten",
  mealNutAllergy: "Alergi kacang",
  submitRsvp: "Kirim RSVP",
  rsvpNotEnabled: "RSVP tidak diaktifkan untuk undangan ini.",
  rsvpDeadlinePassed: "Batas waktu RSVP telah lewat.",
  rsvpNotReady: "RSVP belum siap. Tuan rumah harus menetapkan batas tamu di aplikasi.",
  nameTooLong: "Nama terlalu panjang.",
  invalidData: "Data tidak valid.",
  enterGuestName: "Masukkan setidaknya satu nama tamu.",
  rsvpThankYou: "Terima kasih! RSVP Anda telah kami terima.",
  rsvpSubmitError: "Tidak dapat mengirim. Silakan coba lagi.",
  rsvpSaveChanges: "Simpan perubahan",
  rsvpWithdraw: "Hapus RSVP saya",
  rsvpWithdrawDialogTitle: "Hapus RSVP Anda?",
  rsvpWithdrawDialogBody:
    "Tuan rumah tidak akan melihat tanggapan ini lagi. Jika batas waktu masih memungkinkan, Anda dapat mengirim RSVP baru nanti.",
  rsvpWithdrawConfirm: "Hapus RSVP",
  rsvpWithdrawCancel: "Batal",
  rsvpWithdrawnToast: "RSVP Anda telah dihapus.",
  rsvpSessionNote:
    "Browser ini mengingat tanggapan Anda (masuk anonim). Menghapus data situs atau memakai perangkat lain mungkin mengharuskan Anda merespons lagi.",
  rsvpAuthError: "Tidak dapat memulai sesi. Muat ulang halaman.",
  appPromoText: "Unduh aplikasi iPhone untuk membuat dan mengelola undangan Anda.",
  downloadAppStoreAria: "Unduh di App Store",
  dateTBD: "Tanggal menunggu konfirmasi",
  scheduleStarts: "Mulai",
  scheduleEnds: "Selesai",
  addToCalendar: "Tambahkan ke kalender",
  addToCalendarAria: "Unduh file kalender (.ics) untuk undangan ini",
  songRequestTitle: "Permintaan lagu",
  songRequestIntro:
    "Isi nama Anda, lalu isi hingga lima kotak dengan tautan lagu Spotify atau Apple Music — tempel atau jatuhkan tautannya. Pratinjau singkat muncul setelah tautan dikenali.",
  songRequestRequesterLabel: "Nama Anda",
  songRequestRequesterPlaceholder: "Siapa yang meminta?",
  songRequestLinkLabel: "Tautan lagu",
  songRequestLinkPlaceholder: "URL Spotify atau Apple Music",
  songRequestAddTrack: "Tambah lagu lain",
  songRequestRemoveTrack: "Hapus",
  songRequestPreviewLoading: "Mencari lagu…",
  songRequestPreviewError: "Tautan tidak dapat dibaca. Pastikan itu lagu dari Spotify atau Apple Music.",
  songRequestInvalidLink: "Gunakan tautan lagu Spotify atau Apple Music saja.",
  songRequestLimitReached: "Anda dapat menambahkan maksimal lima lagu.",
  songRequestSubmit: "Kirim permintaan",
  songRequestThankYou: "Terima kasih! Permintaan lagu Anda telah dikirim.",
  songRequestSubmitError: "Tidak dapat mengirim. Silakan coba lagi.",
  songRequestNeedName: "Masukkan nama Anda.",
  songRequestNeedOneResolvedTrack: "Isi setidaknya satu kotak dan tunggu pratinjau muncul.",
  songRequestSpotifyAria: "Spotify",
  songRequestAppleMusicAria: "Apple Music",
  songRequestSlotsCaption:
    "Ketuk kotak, lalu tempel (Ctrl+V / ⌘V) atau jatuhkan tautan. Lima slot — hanya yang terisi yang dikirim.",
  songRequestSlotPasteHint: "Tempel atau jatuhkan",
  songRequestSlotAria: "Slot lagu {n}. Ketuk; tempel atau jatuhkan tautan Spotify atau Apple Music.",
};

const nl: Messages = {
  ...en,
  invitePageTitleFallbackWedding: "Bruiloft",
  metaTitle: "Uitnodiging",
  loading: "Laden…",
  invitationNotFound: "Uitnodiging niet gevonden",
  invalidInviteLink:
    "Open een geldige uitnodigingslink, bijv. /your-slug, /invite/your-slug of ?invite=your-slug op localhost.",
  invitationUnavailable: "Deze uitnodiging bestaat niet of is niet beschikbaar.",
  unknownError: "Onbekende fout",
  noPhoto: "Geen foto",
  noPhotoAria: "Geen foto",
  location: "Locatie",
  about: "Over",
  timeline: "Tijdlijn",
  rsvp: "RSVP",
  lastDay: "Laatste dag",
  countdownDays: "d",
  countdownHours: "u",
  countdownMinutes: "min",
  enjoyCelebration: "Geniet van het feest",
  openAppleMaps: "Route openen in Apple Kaarten",
  openGoogleMaps: "Route openen in Google Maps",
  appleMaps: "Apple Kaarten",
  googleMaps: "Google Maps",
  venueMapAria: "Locatiekaart",
  rsvpCapacityOne: "Met deze uitnodiging zijn maximaal 1 gast toegestaan.",
  rsvpCapacityMany: "Met deze uitnodiging zijn maximaal {n} gasten toegestaan.",
  rsvpHelpText:
    "Voer minstens één naam in. Andere rijen zijn optioneel als u alleen komt.",
  guestNumber: "Gast {n}",
  fullNamePlaceholder: "Volledige naam",
  optionalNamePlaceholder: "Optioneel — volledige naam",
  mealPreferencesOptional: "Maaltijdvoorkeuren (optioneel)",
  mealVegetarian: "Vegetarisch",
  mealVegan: "Veganistisch",
  mealGlutenFree: "Glutenvrij",
  mealNutAllergy: "Notenallergie",
  submitRsvp: "RSVP versturen",
  rsvpNotEnabled: "RSVP is niet ingeschakeld voor deze uitnodiging.",
  rsvpDeadlinePassed: "De RSVP-frist is verstreken.",
  rsvpNotReady: "RSVP is nog niet klaar. De hosts moeten een gastenlimiet instellen in de app.",
  nameTooLong: "De naam is te lang.",
  invalidData: "Ongeldige gegevens.",
  enterGuestName: "Voer minstens één gastnaam in.",
  rsvpThankYou: "Dank je! Je RSVP is ontvangen.",
  rsvpSubmitError: "Verzenden mislukt. Probeer het opnieuw.",
  rsvpSaveChanges: "Wijzigingen opslaan",
  rsvpWithdraw: "Mijn RSVP verwijderen",
  rsvpWithdrawDialogTitle: "Je RSVP verwijderen?",
  rsvpWithdrawDialogBody:
    "De hosts zien dit antwoord niet meer. Als de deadline dat toelaat, kun je later een nieuwe RSVP sturen.",
  rsvpWithdrawConfirm: "RSVP verwijderen",
  rsvpWithdrawCancel: "Annuleren",
  rsvpWithdrawnToast: "Je RSVP is verwijderd.",
  rsvpSessionNote:
    "Deze browser onthoudt je antwoord (anonieme aanmelding). Als je sitegegevens wist of een ander apparaat gebruikt, moet je misschien opnieuw reageren.",
  rsvpAuthError: "Kan de sessie niet starten. Vernieuw de pagina.",
  appPromoText: "Download de iPhone-app om je uitnodigingen te maken en te beheren.",
  downloadAppStoreAria: "Download in de App Store",
  dateTBD: "Datum nog niet bekend",
  scheduleStarts: "Start",
  scheduleEnds: "Einde",
  addToCalendar: "Toevoegen aan agenda",
  addToCalendarAria: "Agendabestand (.ics) voor deze uitnodiging downloaden",
  songRequestTitle: "Muziekaanvragen",
  songRequestIntro:
    "Vul je naam in en vul tot vijf vakjes met Spotify- of Apple Music-songlinks — plak of sleep de link. Na herkenning verschijnt een korte voorbeeldweergave.",
  songRequestRequesterLabel: "Je naam",
  songRequestRequesterPlaceholder: "Wie vraagt het aan?",
  songRequestLinkLabel: "Nummerlink",
  songRequestLinkPlaceholder: "Spotify- of Apple Music-URL",
  songRequestAddTrack: "Nog een nummer toevoegen",
  songRequestRemoveTrack: "Verwijderen",
  songRequestPreviewLoading: "Nummer zoeken…",
  songRequestPreviewError: "Link kan niet worden gelezen. Controleer of het een Spotify- of Apple Music-nummer is.",
  songRequestInvalidLink: "Gebruik alleen links naar Spotify- of Apple Music-nummers.",
  songRequestLimitReached: "Je kunt maximaal vijf nummers toevoegen.",
  songRequestSubmit: "Verzoeken verzenden",
  songRequestThankYou: "Bedankt! Je muziekaanvragen zijn verzonden.",
  songRequestSubmitError: "Verzenden mislukt. Probeer het opnieuw.",
  songRequestNeedName: "Vul je naam in.",
  songRequestNeedOneResolvedTrack: "Vul minstens één vakje en wacht op de voorbeeldweergave.",
  songRequestSpotifyAria: "Spotify",
  songRequestAppleMusicAria: "Apple Music",
  songRequestSlotsCaption:
    "Tik op een vakje, plak daarna (Ctrl+V / ⌘V) of zet een link neer. Vijf plekken — alleen ingevulde worden verstuurd.",
  songRequestSlotPasteHint: "Plakken of neerzetten",
  songRequestSlotAria: "Songvak {n}. Tik; plak of zet een Spotify- of Apple Music-link neer.",
};

const da: Messages = {
  ...en,
  invitePageTitleFallbackWedding: "Bryllup",
  metaTitle: "Invitation",
  loading: "Indlæser…",
  invitationNotFound: "Invitationen blev ikke fundet",
  invalidInviteLink:
    "Åbn et gyldigt invitationslink, f.eks. /dit-slug, /invite/dit-slug eller ?invite=dit-slug på localhost.",
  invitationUnavailable: "Denne invitation findes ikke eller er ikke tilgængelig.",
  unknownError: "Ukendt fejl",
  noPhoto: "Intet foto",
  noPhotoAria: "Intet foto",
  location: "Sted",
  about: "Om",
  timeline: "Tidsplan",
  rsvp: "Tilmelding",
  lastDay: "Sidste dag",
  countdownDays: "d",
  countdownHours: "t",
  countdownMinutes: "min",
  enjoyCelebration: "God fornøjelse",
  openAppleMaps: "Åbn rutevejledning i Apple Maps",
  openGoogleMaps: "Åbn rutevejledning i Google Maps",
  appleMaps: "Apple Maps",
  googleMaps: "Google Maps",
  venueMapAria: "Stedets kort",
  rsvpCapacityOne: "Arrangementet accepterer højst 1 gæst med denne invitation.",
  rsvpCapacityMany: "Arrangementet accepterer højst {n} gæster med denne invitation.",
  rsvpHelpText:
    "Angiv mindst ét navn. De øvrige rækker er valgfrie, hvis du kommer alene.",
  guestNumber: "Gæst {n}",
  fullNamePlaceholder: "Fulde navn",
  optionalNamePlaceholder: "Valgfrit — fulde navn",
  mealPreferencesOptional: "Måltidspræferencer (valgfrit)",
  mealVegetarian: "Vegetar",
  mealVegan: "Vegansk",
  mealGlutenFree: "Glutenfri",
  mealNutAllergy: "Nøddeallergi",
  submitRsvp: "Send tilmelding",
  rsvpNotEnabled: "Tilmelding er ikke aktiveret for denne invitation.",
  rsvpDeadlinePassed: "Tilmeldingsfristen er overskredet.",
  rsvpNotReady: "Tilmelding er ikke klar endnu. Værterne skal angive et gæsteantal i appen.",
  nameTooLong: "Navnet er for langt.",
  invalidData: "Ugyldige data.",
  enterGuestName: "Angiv mindst ét gæstenavn.",
  rsvpThankYou: "Tak! Vi har modtaget din tilmelding.",
  rsvpSubmitError: "Kunne ikke sende. Prøv igen.",
  rsvpSaveChanges: "Gem ændringer",
  rsvpWithdraw: "Slet min tilmelding",
  rsvpWithdrawDialogTitle: "Slet din tilmelding?",
  rsvpWithdrawDialogBody:
    "Værterne ser ikke længere dette svar. Hvis fristen tillader det, kan du sende en ny tilmelding senere.",
  rsvpWithdrawConfirm: "Slet tilmelding",
  rsvpWithdrawCancel: "Annuller",
  rsvpWithdrawnToast: "Din tilmelding er fjernet.",
  rsvpSessionNote:
    "Denne browser husker dit svar (anonymt login). Rydder du webstedsdata eller bruger en anden enhed, skal du måske svare igen.",
  rsvpAuthError: "Kunne ikke starte sessionen. Opdater siden.",
  appPromoText: "Download iPhone-appen for at oprette og administrere dine invitationer.",
  downloadAppStoreAria: "Download på App Store",
  dateTBD: "Dato følger",
  scheduleStarts: "Starter",
  scheduleEnds: "Slutter",
  addToCalendar: "Føj til kalender",
  addToCalendarAria: "Download kalenderfil (.ics) for denne invitation",
  songRequestTitle: "Musikønsker",
  songRequestIntro:
    "Skriv dit navn og udfyld op til fem felter med Spotify- eller Apple Music-nummerlinks — indsæt eller slip linket. En kort forhåndsvisning vises, når linket genkendes.",
  songRequestRequesterLabel: "Dit navn",
  songRequestRequesterPlaceholder: "Hvem ønsker?",
  songRequestLinkLabel: "Nummerlink",
  songRequestLinkPlaceholder: "Spotify- eller Apple Music-URL",
  songRequestAddTrack: "Tilføj endnu et nummer",
  songRequestRemoveTrack: "Fjern",
  songRequestPreviewLoading: "Søger efter nummer…",
  songRequestPreviewError: "Linket kunne ikke læses. Kontroller, at det er et Spotify- eller Apple Music-nummer.",
  songRequestInvalidLink: "Brug kun links til Spotify- eller Apple Music-numre.",
  songRequestLimitReached: "Du kan tilføje højst fem numre.",
  songRequestSubmit: "Send ønsker",
  songRequestThankYou: "Tak! Dine musikønsker er sendt.",
  songRequestSubmitError: "Kunne ikke sende. Prøv igen.",
  songRequestNeedName: "Angiv dit navn.",
  songRequestNeedOneResolvedTrack: "Udfyld mindst ét felt og vent på forhåndsvisningen.",
  songRequestSpotifyAria: "Spotify",
  songRequestAppleMusicAria: "Apple Music",
  songRequestSlotsCaption:
    "Tryk på et felt, indsæt derefter (Ctrl+V / ⌘V) eller slip et link. Fem pladser — kun udfyldte sendes.",
  songRequestSlotPasteHint: "Indsæt eller slip",
  songRequestSlotAria: "Nummerfelt {n}. Tryk; indsæt eller slip et Spotify- eller Apple Music-link.",
};

const he: Messages = {
  ...en,
  invitePageTitleFallbackWedding: "חתונה",
  metaTitle: "הזמנה",
  loading: "טוען…",
  invitationNotFound: "ההזמנה לא נמצאה",
  invalidInviteLink:
    "פתחו קישור הזמנה תקין, למשל /your-slug, /invite/your-slug או ?invite=your-slug ב-localhost.",
  invitationUnavailable: "ההזמנה הזו לא קיימת או אינה זמינה.",
  unknownError: "שגיאה לא ידועה",
  noPhoto: "אין תמונה",
  noPhotoAria: "אין תמונה",
  location: "מיקום",
  about: "אודות",
  timeline: "לו״ז",
  rsvp: "אישור הגעה",
  lastDay: "יום אחרון",
  countdownDays: "י",
  countdownHours: "ש",
  countdownMinutes: "דק",
  enjoyCelebration: "תיהנו מהחגיגה",
  openAppleMaps: "פתחו ניווט ב‑Apple Maps",
  openGoogleMaps: "פתחו ניווט ב‑Google Maps",
  appleMaps: "Apple Maps",
  googleMaps: "Google Maps",
  venueMapAria: "מפת המקום",
  rsvpCapacityOne: "באירוע זה ניתן לאשר עד אורח אחד עם ההזמנה הזו.",
  rsvpCapacityMany: "באירוע זה ניתן לאשר עד {n} אורחים עם ההזמנה הזו.",
  rsvpHelpText: "הזינו לפחות שם אחד. שאר השורות אופציונליות אם אתם מגיעים לבד.",
  guestNumber: "אורח {n}",
  fullNamePlaceholder: "שם מלא",
  optionalNamePlaceholder: "אופציונלי — שם מלא",
  mealPreferencesOptional: "העדפות ארוחה (אופציונלי)",
  mealVegetarian: "צמחוני",
  mealVegan: "טבעוני",
  mealGlutenFree: "ללא גלוטן",
  mealNutAllergy: "אלרגיה לאגוזים",
  submitRsvp: "שלחו אישור הגעה",
  rsvpNotEnabled: "אישור הגעה אינו זמין להזמנה זו.",
  rsvpDeadlinePassed: "מועד אישור ההגעה חלף.",
  rsvpNotReady: "אישור ההגעה אינו מוכן עדיין. המארחים צריכים להגדיר מגבלת אורחים באפליקציה.",
  nameTooLong: "השם ארוך מדי.",
  invalidData: "נתונים לא תקינים.",
  enterGuestName: "הזינו לפחות שם אורח אחד.",
  rsvpThankYou: "תודה! קיבלנו את אישור ההגעה.",
  rsvpSubmitError: "לא ניתן לשלוח. נסו שוב.",
  rsvpSaveChanges: "שמור שינויים",
  rsvpWithdraw: "מחק את אישור ההגעה שלי",
  rsvpWithdrawDialogTitle: "למחוק את אישור ההגעה?",
  rsvpWithdrawDialogBody:
    "המארחים לא יראו יותר את התשובה הזו. אם המועד עדיין מאפשר, אפשר לשלוח אישור חדש מאוחר יותר.",
  rsvpWithdrawConfirm: "מחק אישור הגעה",
  rsvpWithdrawCancel: "ביטול",
  rsvpWithdrawnToast: "אישור ההגעה הוסר.",
  rsvpSessionNote:
    "הדפדפן הזה זוכר את התשובה (התחברות אנונימית). אם תנקו נתוני אתר או תשתמשו במכשיר אחר, ייתכן שתצטרכו להגיב שוב.",
  rsvpAuthError: "לא ניתן להתחיל את הסשן. רעננו את הדף.",
  appPromoText: "הורידו את אפליקציית ה‑iPhone כדי ליצור ולנהל את ההזמנות שלכם.",
  downloadAppStoreAria: "הורידו ב‑App Store",
  dateTBD: "התאריך ייקבע",
  scheduleStarts: "התחלה",
  scheduleEnds: "סיום",
  addToCalendar: "הוסיפו ליומן",
  addToCalendarAria: "הורידו קובץ יומן (.ics) עבור ההזמנה הזו",
  songRequestTitle: "בקשות שירים",
  songRequestIntro:
    "הזינו את שמכם ומלאו עד חמש משבצות עם קישורי שירים מ‑Spotify או Apple Music — הדביקו או שחררו את הקישור. לאחר זיהוי יוצג תצוגה מקדימה קצרה.",
  songRequestRequesterLabel: "השם שלכם",
  songRequestRequesterPlaceholder: "מי מבקש?",
  songRequestLinkLabel: "קישור לשיר",
  songRequestLinkPlaceholder: "כתובת Spotify או Apple Music",
  songRequestAddTrack: "הוסיפו שיר נוסף",
  songRequestRemoveTrack: "הסרה",
  songRequestPreviewLoading: "מחפשים את השיר…",
  songRequestPreviewError: "לא ניתן לקרוא את הקישור. ודאו שזה שיר מ‑Spotify או Apple Music.",
  songRequestInvalidLink: "השתמשו רק בקישורי שירים מ‑Spotify או Apple Music.",
  songRequestLimitReached: "ניתן להוסיף עד חמישה שירים.",
  songRequestSubmit: "שלחו בקשות",
  songRequestThankYou: "תודה! בקשות השירים נשלחו.",
  songRequestSubmitError: "לא ניתן לשלוח. נסו שוב.",
  songRequestNeedName: "הזינו את שמכם.",
  songRequestNeedOneResolvedTrack: "מלאו לפחות משבצת אחת והמתינו לתצוגה המקדימה.",
  songRequestSpotifyAria: "Spotify",
  songRequestAppleMusicAria: "Apple Music",
  songRequestSlotsCaption:
    "הקישו על משבצת, לאחר מכן הדביקו (Ctrl+V / ⌘V) או שחררו קישור. חמש משבצות — נשלחות רק אלה שמולאו.",
  songRequestSlotPasteHint: "הדבקה או שחרור",
  songRequestSlotAria: "משבצת שיר {n}. הקישו; הדביקו או שחררו קישור Spotify או Apple Music.",
};

const no: Messages = {
  ...en,
  invitePageTitleFallbackWedding: "Bryllup",
  metaTitle: "Invitasjon",
  loading: "Laster…",
  invitationNotFound: "Fant ikke invitasjonen",
  invalidInviteLink:
    "Åpne en gyldig invitasjonslenke, f.eks. /din-slug, /invite/din-slug eller ?invite=din-slug på localhost.",
  invitationUnavailable: "Denne invitasjonen finnes ikke eller er ikke tilgjengelig.",
  unknownError: "Ukjent feil",
  noPhoto: "Ingen bilde",
  noPhotoAria: "Ingen bilde",
  location: "Sted",
  about: "Om",
  timeline: "Program",
  rsvp: "Svar",
  lastDay: "Siste dag",
  countdownDays: "d",
  countdownHours: "t",
  countdownMinutes: "min",
  enjoyCelebration: "God feiring",
  openAppleMaps: "Åpne veibeskrivelse i Apple Maps",
  openGoogleMaps: "Åpne veibeskrivelse i Google Maps",
  appleMaps: "Apple Maps",
  googleMaps: "Google Maps",
  venueMapAria: "Stedskart",
  rsvpCapacityOne: "Arrangementet tar maksimalt 1 gjest med denne invitasjonen.",
  rsvpCapacityMany: "Arrangementet tar maksimalt {n} gjester med denne invitasjonen.",
  rsvpHelpText:
    "Skriv inn minst ett navn. De andre radene er valgfrie hvis du kommer alene.",
  guestNumber: "Gjest {n}",
  fullNamePlaceholder: "Fullt navn",
  optionalNamePlaceholder: "Valgfritt — fullt navn",
  mealPreferencesOptional: "Måltidspreferanser (valgfritt)",
  mealVegetarian: "Vegetar",
  mealVegan: "Vegansk",
  mealGlutenFree: "Glutenfritt",
  mealNutAllergy: "Nøtteallergi",
  submitRsvp: "Send svar",
  rsvpNotEnabled: "Svar er ikke aktivert for denne invitasjonen.",
  rsvpDeadlinePassed: "Svarfristen er passert.",
  rsvpNotReady: "Svar er ikke klart ennå. Vertene må angi et gjesteantall i appen.",
  nameTooLong: "Navnet er for langt.",
  invalidData: "Ugyldige data.",
  enterGuestName: "Skriv inn minst ett gjestenavn.",
  rsvpThankYou: "Takk! Vi har mottatt svaret ditt.",
  rsvpSubmitError: "Kunne ikke sende. Prøv igjen.",
  rsvpSaveChanges: "Lagre endringer",
  rsvpWithdraw: "Slett mitt svar",
  rsvpWithdrawDialogTitle: "Slette svaret ditt?",
  rsvpWithdrawDialogBody:
    "Vertene ser ikke lenger dette svaret. Hvis fristen tillater det, kan du sende et nytt svar senere.",
  rsvpWithdrawConfirm: "Slett svar",
  rsvpWithdrawCancel: "Avbryt",
  rsvpWithdrawnToast: "Svaret ditt er fjernet.",
  rsvpSessionNote:
    "Denne nettleseren husker svaret ditt (anonym pålogging). Hvis du sletter nettstedsdata eller bruker en annen enhet, kan du måtte svare på nytt.",
  rsvpAuthError: "Kunne ikke starte økten. Oppdater siden.",
  appPromoText: "Last ned iPhone-appen for å lage og administrere invitasjonene dine.",
  downloadAppStoreAria: "Last ned på App Store",
  dateTBD: "Dato kommer",
  scheduleStarts: "Starter",
  scheduleEnds: "Slutter",
  addToCalendar: "Legg til i kalender",
  addToCalendarAria: "Last ned kalenderfil (.ics) for denne invitasjonen",
  songRequestTitle: "Musikkønsker",
  songRequestIntro:
    "Skriv navnet ditt og fyll opptil fem felt med Spotify- eller Apple Music-lenker — lim inn eller slipp lenken. En kort forhåndsvisning vises når lenken gjenkjennes.",
  songRequestRequesterLabel: "Navnet ditt",
  songRequestRequesterPlaceholder: "Hvem ønsker?",
  songRequestLinkLabel: "Sporlenke",
  songRequestLinkPlaceholder: "Spotify- eller Apple Music-URL",
  songRequestAddTrack: "Legg til en sang til",
  songRequestRemoveTrack: "Fjern",
  songRequestPreviewLoading: "Søker etter spor…",
  songRequestPreviewError: "Kunne ikke lese lenken. Sjekk at det er en Spotify- eller Apple Music-sang.",
  songRequestInvalidLink: "Bruk bare lenker til Spotify- eller Apple Music-sanger.",
  songRequestLimitReached: "Du kan legge til maks fem sanger.",
  songRequestSubmit: "Send ønsker",
  songRequestThankYou: "Takk! Musikønskene dine er sendt.",
  songRequestSubmitError: "Kunne ikke sende. Prøv igjen.",
  songRequestNeedName: "Skriv inn navnet ditt.",
  songRequestNeedOneResolvedTrack: "Fyll ut minst ett felt og vent til forhåndsvisningen vises.",
  songRequestSpotifyAria: "Spotify",
  songRequestAppleMusicAria: "Apple Music",
  songRequestSlotsCaption:
    "Trykk på et felt, lim deretter inn (Ctrl+V / ⌘V) eller slipp en lenke. Fem plasser — bare utfylte sendes.",
  songRequestSlotPasteHint: "Lim inn eller slipp",
  songRequestSlotAria: "Sangfelt {n}. Trykk; lim inn eller slipp en Spotify- eller Apple Music-lenke.",
};

const sk: Messages = {
  ...en,
  invitePageTitleFallbackWedding: "Svadba",
  metaTitle: "Pozvánka",
  loading: "Načítava sa…",
  invitationNotFound: "Pozvánka sa nenašla",
  invalidInviteLink:
    "Otvorte platný odkaz na pozvánku, napr. /vas-slug, /invite/vas-slug alebo ?invite=vas-slug na localhost.",
  invitationUnavailable: "Táto pozvánka neexistuje alebo nie je dostupná.",
  unknownError: "Neznáma chyba",
  noPhoto: "Bez fotky",
  noPhotoAria: "Bez fotky",
  location: "Miesto",
  about: "O akcii",
  timeline: "Program",
  rsvp: "Odpoveď",
  lastDay: "Posledný deň",
  countdownDays: "d",
  countdownHours: "h",
  countdownMinutes: "min",
  enjoyCelebration: "Prajeme príjemnú oslavu",
  openAppleMaps: "Otvoriť navigáciu v Apple Maps",
  openGoogleMaps: "Otvoriť navigáciu v Google Maps",
  appleMaps: "Apple Maps",
  googleMaps: "Google Maps",
  venueMapAria: "Mapa miesta",
  rsvpCapacityOne: "Na tejto pozvánke je povolený najviac 1 hosť.",
  rsvpCapacityMany: "Na tejto pozvánke je povolených najviac {n} hostí.",
  rsvpHelpText:
    "Zadajte aspoň jedno meno. Ostatné riadky sú voliteľné, ak prídete sami.",
  guestNumber: "Hosť {n}",
  fullNamePlaceholder: "Celé meno",
  optionalNamePlaceholder: "Voliteľné — celé meno",
  mealPreferencesOptional: "Preferencie stravy (voliteľné)",
  mealVegetarian: "Vegetariánske",
  mealVegan: "Vegánske",
  mealGlutenFree: "Bez lepku",
  mealNutAllergy: "Alergia na orechy",
  submitRsvp: "Odoslať odpoveď",
  rsvpNotEnabled: "Pre túto pozvánku nie je odpoveď zapnutá.",
  rsvpDeadlinePassed: "Lehota na odpoveď uplynula.",
  rsvpNotReady: "Odpoveď ešte nie je pripravená. Hostitelia musia v aplikácii nastaviť limit hostí.",
  nameTooLong: "Meno je príliš dlhé.",
  invalidData: "Neplatné údaje.",
  enterGuestName: "Zadajte aspoň jedno meno hosťa.",
  rsvpThankYou: "Ďakujeme! Vašu odpoveď sme prijali.",
  rsvpSubmitError: "Odoslanie zlyhalo. Skúste znova.",
  rsvpSaveChanges: "Uložiť zmeny",
  rsvpWithdraw: "Odstrániť moju odpoveď",
  rsvpWithdrawDialogTitle: "Odstrániť vašu odpoveď?",
  rsvpWithdrawDialogBody:
    "Hostitelia už túto odpoveď neuvidia. Ak lehota ešte platí, môžete neskôr poslať novú odpoveď.",
  rsvpWithdrawConfirm: "Odstrániť odpoveď",
  rsvpWithdrawCancel: "Zrušiť",
  rsvpWithdrawnToast: "Vaša odpoveď bola odstránená.",
  rsvpSessionNote:
    "Tento prehliadač si pamätá vašu odpoveď (anonymné prihlásenie). Po vymazaní údajov stránky alebo na inom zariadení môže byť potrebné odpovedať znova.",
  rsvpAuthError: "Nepodarilo sa spustiť reláciu. Obnovte stránku.",
  appPromoText: "Stiahnite si aplikáciu pre iPhone a vytvárajte a spravujte pozvánky.",
  downloadAppStoreAria: "Stiahnuť v App Store",
  dateTBD: "Dátum upresníme",
  scheduleStarts: "Začiatok",
  scheduleEnds: "Koniec",
  addToCalendar: "Pridať do kalendára",
  addToCalendarAria: "Stiahnuť súbor kalendára (.ics) pre túto pozvánku",
  songRequestTitle: "Želania skladieb",
  songRequestIntro:
    "Zadajte svoje meno a vyplňte až päť polí odkazmi na skladby zo Spotify alebo Apple Music — vložte alebo pretiahnite odkaz. Po rozpoznaní sa zobrazí krátky náhľad.",
  songRequestRequesterLabel: "Vaše meno",
  songRequestRequesterPlaceholder: "Kto žiada?",
  songRequestLinkLabel: "Odkaz na skladbu",
  songRequestLinkPlaceholder: "URL Spotify alebo Apple Music",
  songRequestAddTrack: "Pridať ďalšiu skladbu",
  songRequestRemoveTrack: "Odstrániť",
  songRequestPreviewLoading: "Hľadá sa skladba…",
  songRequestPreviewError: "Odkaz sa nepodarilo prečítať. Skontrolujte, či ide o skladbu zo Spotify alebo Apple Music.",
  songRequestInvalidLink: "Používajte len odkazy na skladby zo Spotify alebo Apple Music.",
  songRequestLimitReached: "Môžete pridať najviac päť skladieb.",
  songRequestSubmit: "Odoslať želania",
  songRequestThankYou: "Ďakujeme! Vaše želania boli odoslané.",
  songRequestSubmitError: "Odoslanie zlyhalo. Skúste znova.",
  songRequestNeedName: "Zadajte svoje meno.",
  songRequestNeedOneResolvedTrack: "Vyplňte aspoň jedno pole a počkajte na náhľad.",
  songRequestSpotifyAria: "Spotify",
  songRequestAppleMusicAria: "Apple Music",
  songRequestSlotsCaption:
    "Ťuknite na pole, potom vložte (Ctrl+V / ⌘V) alebo pretiahnite odkaz. Päť miest — odosielajú sa len vyplnené.",
  songRequestSlotPasteHint: "Vložiť alebo pretiahnuť",
  songRequestSlotAria: "Pole skladby {n}. Ťuknite; vložte alebo pretiahnite odkaz Spotify alebo Apple Music.",
};

const uk: Messages = {
  ...en,
  invitePageTitleFallbackWedding: "Весілля",
  metaTitle: "Запрошення",
  loading: "Завантаження…",
  invitationNotFound: "Запрошення не знайдено",
  invalidInviteLink:
    "Відкрийте дійсне посилання на запрошення, напр. /your-slug, /invite/your-slug або ?invite=your-slug на localhost.",
  invitationUnavailable: "Цього запрошення не існує або воно недоступне.",
  unknownError: "Невідома помилка",
  noPhoto: "Немає фото",
  noPhotoAria: "Немає фото",
  location: "Місце",
  about: "Про подію",
  timeline: "Програма",
  rsvp: "Відповідь",
  lastDay: "Останній день",
  countdownDays: "д",
  countdownHours: "год",
  countdownMinutes: "хв",
  enjoyCelebration: "Гарної вам святкової атмосфери",
  openAppleMaps: "Відкрити маршрут у Apple Maps",
  openGoogleMaps: "Відкрити маршрут у Google Maps",
  appleMaps: "Apple Maps",
  googleMaps: "Google Maps",
  venueMapAria: "Карта місця",
  rsvpCapacityOne: "За цим запрошенням приймається щонайбільше 1 гість.",
  rsvpCapacityMany: "За цим запрошенням приймається щонайбільше {n} гостей.",
  rsvpHelpText:
    "Введіть принаймні одне ім’я. Інші рядки необов’язкові, якщо ви приходите самі.",
  guestNumber: "Гість {n}",
  fullNamePlaceholder: "Повне ім’я",
  optionalNamePlaceholder: "Необов’язково — повне ім’я",
  mealPreferencesOptional: "Харчові побажання (необов’язково)",
  mealVegetarian: "Вегетаріанське",
  mealVegan: "Веганське",
  mealGlutenFree: "Без глютену",
  mealNutAllergy: "Алергія на горіхи",
  submitRsvp: "Надіслати відповідь",
  rsvpNotEnabled: "Відповідь для цього запрошення вимкнено.",
  rsvpDeadlinePassed: "Термін відповіді минув.",
  rsvpNotReady: "Відповідь ще не готова. Організатори мають встановити ліміт гостей у застосунку.",
  nameTooLong: "Ім’я занадто довге.",
  invalidData: "Некоректні дані.",
  enterGuestName: "Введіть принаймні одне ім’я гостя.",
  rsvpThankYou: "Дякуємо! Ми отримали вашу відповідь.",
  rsvpSubmitError: "Не вдалося надіслати. Спробуйте ще раз.",
  rsvpSaveChanges: "Зберегти зміни",
  rsvpWithdraw: "Видалити мою відповідь",
  rsvpWithdrawDialogTitle: "Видалити вашу відповідь?",
  rsvpWithdrawDialogBody:
    "Організатори більше не бачитимуть цю відповідь. Якщо термін дозволяє, пізніше можна надіслати нову відповідь.",
  rsvpWithdrawConfirm: "Видалити відповідь",
  rsvpWithdrawCancel: "Скасувати",
  rsvpWithdrawnToast: "Вашу відповідь видалено.",
  rsvpSessionNote:
    "Цей браузер запам’ятовує відповідь (анонімний вхід). Якщо очистити дані сайту або зайти з іншого пристрою, може знадобитися відповісти знову.",
  rsvpAuthError: "Не вдалося почати сеанс. Оновіть сторінку.",
  appPromoText: "Завантажте застосунок для iPhone, щоб створювати й керувати запрошеннями.",
  downloadAppStoreAria: "Завантажити в App Store",
  dateTBD: "Дату буде уточнено",
  scheduleStarts: "Початок",
  scheduleEnds: "Кінець",
  addToCalendar: "Додати до календаря",
  addToCalendarAria: "Завантажити файл календаря (.ics) для цього запрошення",
  songRequestTitle: "Музичні побажання",
  songRequestIntro:
    "Введіть своє ім’я й заповніть до п’яти полів посиланнями на треки Spotify або Apple Music — вставте або перетягніть посилання. Після розпізнавання з’явиться короткий перегляд.",
  songRequestRequesterLabel: "Ваше ім’я",
  songRequestRequesterPlaceholder: "Хто замовляє?",
  songRequestLinkLabel: "Посилання на трек",
  songRequestLinkPlaceholder: "URL Spotify або Apple Music",
  songRequestAddTrack: "Додати ще один трек",
  songRequestRemoveTrack: "Видалити",
  songRequestPreviewLoading: "Пошук треку…",
  songRequestPreviewError: "Не вдалося прочитати посилання. Переконайтеся, що це трек Spotify або Apple Music.",
  songRequestInvalidLink: "Використовуйте лише посилання на треки Spotify або Apple Music.",
  songRequestLimitReached: "Можна додати щонайбільше п’ять треків.",
  songRequestSubmit: "Надіслати побажання",
  songRequestThankYou: "Дякуємо! Ваші побажання надіслано.",
  songRequestSubmitError: "Не вдалося надіслати. Спробуйте ще раз.",
  songRequestNeedName: "Введіть своє ім’я.",
  songRequestNeedOneResolvedTrack: "Заповніть принаймні одне поле та дочекайтеся перегляду.",
  songRequestSpotifyAria: "Spotify",
  songRequestAppleMusicAria: "Apple Music",
  songRequestSlotsCaption:
    "Торкніться поля, потім вставте (Ctrl+V / ⌘V) або перетягніть посилання. П’ять слотів — надсилаються лише заповнені.",
  songRequestSlotPasteHint: "Вставити або перетягнути",
  songRequestSlotAria: "Слот треку {n}. Торкніться; вставте або перетягніть посилання Spotify або Apple Music.",
};

const vi: Messages = {
  ...en,
  invitePageTitleFallbackWedding: "Đám cưới",
  metaTitle: "Thiệp mời",
  loading: "Đang tải…",
  invitationNotFound: "Không tìm thấy thiệp mời",
  invalidInviteLink:
    "Hãy mở liên kết mời hợp lệ, ví dụ /your-slug, /invite/your-slug hoặc ?invite=your-slug trên localhost.",
  invitationUnavailable: "Thiệp mời này không tồn tại hoặc không khả dụng.",
  unknownError: "Lỗi không xác định",
  noPhoto: "Không có ảnh",
  noPhotoAria: "Không có ảnh",
  location: "Địa điểm",
  about: "Giới thiệu",
  timeline: "Lịch trình",
  rsvp: "Phản hồi",
  lastDay: "Ngày cuối",
  countdownDays: "ng",
  countdownHours: "g",
  countdownMinutes: "ph",
  enjoyCelebration: "Chúc bạn một buổi lễ vui vẻ",
  openAppleMaps: "Mở chỉ đường trong Apple Maps",
  openGoogleMaps: "Mở chỉ đường trong Google Maps",
  appleMaps: "Apple Maps",
  googleMaps: "Google Maps",
  venueMapAria: "Bản đồ địa điểm",
  rsvpCapacityOne: "Sự kiện này chỉ tiếp tối đa 1 khách với thiệp mời này.",
  rsvpCapacityMany: "Sự kiện này chỉ tiếp tối đa {n} khách với thiệp mời này.",
  rsvpHelpText:
    "Vui lòng nhập ít nhất một tên. Các dòng khác là tùy chọn nếu bạn đi một mình.",
  guestNumber: "Khách {n}",
  fullNamePlaceholder: "Họ và tên",
  optionalNamePlaceholder: "Tùy chọn — họ và tên",
  mealPreferencesOptional: "Sở thích ăn uống (tùy chọn)",
  mealVegetarian: "Ăn chay",
  mealVegan: "Thuần chay",
  mealGlutenFree: "Không gluten",
  mealNutAllergy: "Dị ứng hạt",
  submitRsvp: "Gửi phản hồi",
  rsvpNotEnabled: "Chưa bật phản hồi cho thiệp mời này.",
  rsvpDeadlinePassed: "Đã quá hạn phản hồi.",
  rsvpNotReady: "Chưa thể phản hồi. Chủ tiệc cần đặt giới hạn khách trong ứng dụng.",
  nameTooLong: "Tên quá dài.",
  invalidData: "Dữ liệu không hợp lệ.",
  enterGuestName: "Vui lòng nhập ít nhất một tên khách.",
  rsvpThankYou: "Cảm ơn bạn! Chúng tôi đã nhận được phản hồi.",
  rsvpSubmitError: "Không gửi được. Vui lòng thử lại.",
  rsvpSaveChanges: "Lưu thay đổi",
  rsvpWithdraw: "Xóa phản hồi của tôi",
  rsvpWithdrawDialogTitle: "Xóa phản hồi?",
  rsvpWithdrawDialogBody:
    "Chủ tiệc sẽ không còn thấy phản hồi này. Nếu vẫn trong hạn, bạn có thể gửi phản hồi mới sau.",
  rsvpWithdrawConfirm: "Xóa phản hồi",
  rsvpWithdrawCancel: "Hủy",
  rsvpWithdrawnToast: "Đã xóa phản hồi của bạn.",
  rsvpSessionNote:
    "Trình duyệt này ghi nhớ phản hồi (đăng nhập ẩn danh). Nếu xóa dữ liệu trang web hoặc dùng thiết bị khác, bạn có thể cần phản hồi lại.",
  rsvpAuthError: "Không thể bắt đầu phiên. Vui lòng tải lại trang.",
  appPromoText: "Tải ứng dụng iPhone để tạo và quản lý thiệp mời.",
  downloadAppStoreAria: "Tải trên App Store",
  dateTBD: "Ngày chưa xác định",
  scheduleStarts: "Bắt đầu",
  scheduleEnds: "Kết thúc",
  addToCalendar: "Thêm vào lịch",
  addToCalendarAria: "Tải xuống tệp lịch (.ics) cho thiệp mời này",
  songRequestTitle: "Yêu cầu bài hát",
  songRequestIntro:
    "Nhập tên của bạn, rồi điền tối đa năm ô bằng liên kết bài Spotify hoặc Apple Music — dán hoặc thả liên kết. Khi nhận diện được sẽ có bản xem trước ngắn.",
  songRequestRequesterLabel: "Tên của bạn",
  songRequestRequesterPlaceholder: "Ai yêu cầu?",
  songRequestLinkLabel: "Liên kết bài hát",
  songRequestLinkPlaceholder: "URL Spotify hoặc Apple Music",
  songRequestAddTrack: "Thêm bài hát",
  songRequestRemoveTrack: "Xóa",
  songRequestPreviewLoading: "Đang tìm bài hát…",
  songRequestPreviewError: "Không đọc được liên kết. Hãy đảm bảo đây là bài từ Spotify hoặc Apple Music.",
  songRequestInvalidLink: "Chỉ dùng liên kết bài hát Spotify hoặc Apple Music.",
  songRequestLimitReached: "Bạn chỉ có thể thêm tối đa năm bài.",
  songRequestSubmit: "Gửi yêu cầu",
  songRequestThankYou: "Cảm ơn bạn! Yêu cầu bài hát đã được gửi.",
  songRequestSubmitError: "Không gửi được. Vui lòng thử lại.",
  songRequestNeedName: "Vui lòng nhập tên.",
  songRequestNeedOneResolvedTrack: "Điền ít nhất một ô và đợi bản xem trước hiển thị.",
  songRequestSpotifyAria: "Spotify",
  songRequestAppleMusicAria: "Apple Music",
  songRequestSlotsCaption:
    "Chạm một ô, sau đó dán (Ctrl+V / ⌘V) hoặc thả liên kết. Năm ô — chỉ gửi những ô đã điền.",
  songRequestSlotPasteHint: "Dán hoặc thả",
  songRequestSlotAria: "Ô bài hát {n}. Chạm; dán hoặc thả liên kết Spotify hoặc Apple Music.",
};

export const TRANSLATIONS: Record<AppLocale, Messages> = {
  en,
  ar,
  "zh-Hans": zhHans,
  da,
  nl,
  fr,
  de,
  he,
  hi,
  id,
  it,
  ja,
  no,
  "pt-PT": ptPT,
  ru,
  sk,
  es,
  tr,
  uk,
  vi,
};
